import { isNextResponse, type NextResponse } from "@/lib/next/types";
import type { TaskSummary, TaskCreatePayload, TaskUpdatePayload } from "@/types/tasks";
import type {
  Goal,
  GoalActivityItem,
  GoalContextItem,
  GoalContextItemCreatePayload,
  GoalContextItemUpdatePayload,
  GoalCreatePayload,
  GoalMessage,
  GoalRun,
  GoalUpdatePayload,
} from "@/types/goals";
import type { WorkspaceRepository } from "@/types/workspaces";
import type { NamespaceSummary, NamespaceId, NamespaceAgentState, NamespaceDatabaseState, NamespaceProvisioning, SlackInstallation, BranchAdoptionRule, ChangeDeliveryMode, NotificationChannel, NotificationChannelPayload } from "@/types/namespaces";
import type {
  CronJob,
  CronJobCreatorKind,
  CronJobRun,
  CronJobRunListQuery,
  CronJobRunListResponse,
  CronJobRunResult,
  CronJobStatus,
  CronJobKindDescriptor,
  CreateCronJobPayload,
  UpdateCronJobPayload,
} from "@/types/cron-jobs";
import type { WorkspaceTaskThreadResponse, TaskThreadEventDto } from "@/types/task-thread";
import type {
  WorkspaceDocumentContentResponse,
  WorkspaceDocumentsTreeResponse,
} from "@/types/documents";
import type { WorkspaceSearchResponse } from "@/types/workspace-search";
import { buildAgentApiUrl } from "@/lib/api-config";
import { markSubscriptionLocked } from "@/lib/billing-lock";
import type {
  RepositoryBranch,
  RepositoryCommitListResponse,
  RepositoryDiffResponse,
  RepositoryFileContent,
  RepositoryFileDiffResponse,
  RepositoryRefreshResponse,
  RepositoryTreeResponse,
} from "@/types/repository-content";
import type {
  CommitActivityBucketSize as ChangesCommitActivityBucketSize,
  CommitActivityResponse as ChangesCommitActivityResponse,
  CommitListResponse as ChangesCommitListResponse,
  CommitRecord as ChangesCommitRecord,
} from "@/types/changes";
import type {
  ChangelogRun,
  ChangelogRunListResponse,
  ChangelogRunQueued,
  ChangelogRunRequest,
} from "@/types/changelog";
import type { BlastRadiusResponse, ImpactFromItemResponse, QaRetestResponse } from "@/types/change-impact";
import type {
  WorkspacePreferenceDto,
  WorkspacePreferenceScope,
  WorkspacePreferenceUpdateRequest,
} from "@/types/workspace-preferences";

export function fetchNamespaceProviderIssue(namespaceId: string) {
  return fetchJson<import("@/types/provider-issue").NamespaceProviderIssueResponse>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/provider-issue`,
  );
}

export type ApiErrorOptions = {
  /** Short machine-readable code from the response body's `error` field, e.g. "seat_limit_reached". */
  code?: string;
  /** The parsed JSON body, when there was one. Lets callers read extra fields such as `limit`. */
  body?: unknown;
};

export class ApiError extends Error {
  status: number;
  code?: string;
  body?: unknown;

  constructor(message: string, status: number, options: ApiErrorOptions = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = options.code;
    this.body = options.body;
  }
}

/** True when `error` is an ApiError carrying the given `{ error: code }` body. */
export function isApiErrorCode(error: unknown, code: string): error is ApiError {
  return error instanceof ApiError && error.code === code;
}

export function isUnauthorizedError(error: unknown) {
  return (
    error instanceof ApiError &&
    (error.status === 401 || error.status === 403 || error.status === 307)
  );
}

export interface NamespaceTasksResponse {
  namespaceId: NamespaceId;
  tasks: TaskSummary[];
  /** Total number of tasks matching the filters across all pages. */
  total: number;
  page: number;
  pageSize: number;
}

export type NamespaceTaskListQuery = {
  state?: "active" | "pending_review" | "done" | "archived" | "all";
  search?: string;
  sort?: string;
  labels?: string[];
  /** Inclusive ISO-8601 lower bound on the task's created timestamp. */
  createdFrom?: string;
  /** Inclusive ISO-8601 upper bound on the task's created timestamp. */
  createdTo?: string;
  page?: number;
  pageSize?: number;
};

export type TaskClosePayload = {
  reason?: string;
  source?: string;
};

export type TaskReopenPayload = {
  note?: string;
  source?: string;
};

export type TaskRestartPayload = {
  note?: string;
  source?: string;
};

// The externalDocumentation group's inner keys are snake_case on the wire in
// both outer casings ([JsonPropertyName] on the C# members), so the inherited
// camelCase shape from NamespaceSummary would be wrong — omit and re-declare.
interface RawExternalDocumentationSettings {
  allow_agent_writes?: boolean;
  write_log_retention_days?: number | null;
}

interface RawNamespaceSummary extends Omit<Partial<NamespaceSummary>, "analysis" | "externalDocumentation"> {
  id?: string;
  // snake_case variants from the API's [JsonPropertyName] attributes
  allow_agent_to_edit?: boolean;
  agent_state?: NamespaceAgentState;
  database_state?: NamespaceDatabaseState;
  currently_active_session_id?: string;
  auto_dependencies?: { enabled: boolean } | null;
  auto_review?: {
    enabled: boolean;
    treat_auto_reviewed_as_reviewed: boolean;
    max_auto_review_attempts: number;
  } | null;
  branch_adoption?: { enabled: boolean; max_branches_per_cycle: number; rules?: { pattern: string; kind: string; description?: string | null }[] | null } | null;
  analysis?: {
    ignored_paths?: string[] | null;
    archived_paths?: string[] | null;
    max_file_size_kb?: number | null;
    governance_extraction_enabled?: boolean | null;
    triggers?: {
      startup?: boolean | null;
      file_write?: boolean | null;
      checkout?: boolean | null;
      checkout_debounce_minutes?: number | null;
    } | null;
    documentation_paths?: {
      project_paths?: string[] | null;
      user_facing_paths?: string[] | null;
      third_party_paths?: string[] | null;
    } | null;
    documentation_class_paths?: { class_slug?: string | null; patterns?: string[] | null }[] | null;
    documentation_class_llm_enabled?: boolean | null;
    documentation_class_llm_max_docs_per_run?: number | null;
    infrastructure_as_code_paths?: string[] | null;
    configuration_paths?: string[] | null;
    max_docs_crawl_depth?: number | null;
    library_catalog_enabled?: boolean | null;
    library_docs_enabled?: boolean | null;
    library_docs_max_pages_per_run?: number | null;
    library_docs_max_pages_per_library?: number | null;
    library_docs_include_transitive?: boolean | null;
    library_cmake_llm_fallback_enabled?: boolean | null;
    library_outdated_check_enabled?: boolean | null;
    library_outdated_check_cron?: string | null;
    library_advisory_check_enabled?: boolean | null;
    library_advisory_check_cron?: string | null;
    data_entity_graph_enabled?: boolean | null;
    route_extraction_enabled?: boolean | null;
    event_flow_enabled?: boolean | null;
    edge_inference_enabled?: boolean | null;
    api_spec_extraction_enabled?: boolean | null;
    integration_ai_links_enabled?: boolean | null;
    integration_prose_extraction_enabled?: boolean | null;
    governance_analysis_after_indexing?: boolean | null;
    ai_system_detection_enabled?: boolean | null;
    ownership_analysis_enabled?: boolean | null;
  } | null;
  vcs_commit_notifications?: { enabled?: boolean } | null;
  change_delivery?: {
    mode?: ChangeDeliveryMode;
    draft_until_approved?: boolean;
    auto_merge_approved_prs?: boolean;
    target_branch?: string | null;
  } | null;
  externalDocumentation?: RawExternalDocumentationSettings | null;
  external_documentation?: RawExternalDocumentationSettings | null;
  has_writable_repository?: boolean;
  root_directory?: string;
  source_directory?: string;
  namespace_id?: string;
  organization_id?: string;
  organization_name?: string;
  description?: string;
  icon?: string;
  created_at?: string;
}

export interface UpdateNamespaceSettingsPayload {
  allowAgentToEdit?: boolean;
  agentState?: NamespaceAgentState;
  name?: string;
  description?: string;
  icon?: string;
  autoDependencies?: { enabled: boolean };
  autoReview?: {
    enabled: boolean;
    treatAutoReviewedAsReviewed: boolean;
    maxAutoReviewAttempts: number;
  };
  branchAdoption?: { enabled: boolean; maxBranchesPerCycle: number; rules: BranchAdoptionRule[] };
  analysis?: {
    ignoredPaths: string[];
    archivedPaths: string[];
    maxFileSizeKb: number | null;
    governanceExtractionEnabled: boolean;
    triggers: { startup: boolean; fileWrite: boolean; checkout: boolean; checkoutDebounceMinutes: number };
    documentationPaths?: {
      projectPaths: string[];
      userFacingPaths: string[];
      thirdPartyPaths: string[];
    };
    documentationClassPaths?: { classSlug: string; patterns: string[] }[];
    documentationClassLlmEnabled?: boolean;
    documentationClassLlmMaxDocsPerRun?: number | null;
    infrastructureAsCodePaths?: string[];
    configurationPaths?: string[];
    maxDocsCrawlDepth?: number | null;
    libraryCatalogEnabled?: boolean;
    libraryDocsEnabled?: boolean;
    libraryDocsMaxPagesPerRun?: number | null;
    libraryDocsMaxPagesPerLibrary?: number | null;
    libraryDocsIncludeTransitive?: boolean;
    libraryCMakeLlmFallbackEnabled?: boolean;
    libraryOutdatedCheckEnabled?: boolean;
    libraryOutdatedCheckCron?: string;
    libraryAdvisoryCheckEnabled?: boolean;
    libraryAdvisoryCheckCron?: string;
    dataEntityGraphEnabled?: boolean;
    routeExtractionEnabled?: boolean;
    eventFlowEnabled?: boolean;
    edgeInferenceEnabled?: boolean;
    apiSpecExtractionEnabled?: boolean;
    integrationAiLinksEnabled?: boolean;
    integrationProseExtractionEnabled?: boolean;
    governanceAnalysisAfterIndexing?: boolean;
    aiSystemDetectionEnabled?: boolean;
    ownershipAnalysisEnabled?: boolean;
  };
  vcsCommitNotifications?: { enabled: boolean };
  changeDelivery?: {
    mode: ChangeDeliveryMode;
    draftUntilApproved: boolean;
    autoMergeApprovedPrs: boolean;
    targetBranch: string | null;
  };
  externalDocumentation?: {
    allowAgentWrites: boolean;
    writeLogRetentionDays: number | null;
  };
}

interface RepositoryCredentialPayload {
  kind: string;
  secretName?: string | null;
  privateKey?: string | null;
  username?: string | null;
  publicKeyFingerprint?: string | null;
  notes?: string | null;
}

export interface RepositoryPayload {
  name: string;
  type: string;
  address: string;
  defaultBranch?: string;
  description?: string | null;
  mirrorAuthorName?: string | null;
  mirrorAuthorEmail?: string | null;
  credential?: RepositoryCredentialPayload | null;
  tags?: string[];
}

export interface RepositoryTreeRequestOptions {
  path?: string;
  branch?: string;
  commit?: string;
  filter?: string;
  pageSize?: number;
  pageToken?: string;
  signal?: AbortSignal;
}

export interface RepositoryFileRequestOptions {
  path: string;
  branch?: string;
  commit?: string;
  signal?: AbortSignal;
}

export interface RepositoryCommitsRequestOptions {
  branch?: string;
  pageSize?: number;
  pageToken?: string;
  signal?: AbortSignal;
}

export interface RepositoryDiffRequestOptions {
  head: string;
  base?: string;
  path?: string;
  signal?: AbortSignal;
}

export interface RepositoryRefreshPayload {
  branch?: string;
  signal?: AbortSignal;
}

export interface WorkspaceSemanticSearchPayload {
  query: string;
  limit?: number;
  signal?: AbortSignal;
}

type ParsedError = { message: string; code?: string; body?: unknown };

async function parseError(response: Response): Promise<ParsedError> {
  const fallback = `Request failed (${response.status})`;
  try {
    const payload = await response.clone().json();
    if (typeof payload === "string") {
      return { message: payload };
    }

    const code = typeof payload?.error === "string" ? (payload.error as string) : undefined;
    const withBody = (message: string): ParsedError => ({ message, code, body: payload });

    if (payload?.message) {
      return withBody(payload.message as string);
    }

    if (payload?.detail) {
      return withBody(payload.detail as string);
    }

    if (payload?.title) {
      return withBody(payload.title as string);
    }

    if (payload?.errors) {
      const firstError = Object.values(payload.errors)[0] as
        | string[]
        | undefined;
      if (firstError?.length) {
        return withBody(firstError[0]);
      }
    }

    return withBody(fallback);
  } catch {
    return { message: fallback };
  }
}

function buildProxyPath(path: string) {
  return buildAgentApiUrl(path);
}

// Exported so the cross-tab account-switch watcher (lib/session/account-switch)
// can filter `storage` events down to this key.
export const ACCESS_TOKEN_STORAGE_KEY = "scirix.access_token";
const TOKEN_STORAGE_KEY = ACCESS_TOKEN_STORAGE_KEY;

// "sync" = another tab of the same account rotated the token and this tab
// adopted it from localStorage. Unlike "refresh" it must NOT re-push the cookie:
// the tab that did the refresh already did, and every other tab would otherwise
// PATCH the same token again.
export type AccessTokenUpdateSource = "login" | "refresh" | "logout" | "manual" | "sync";

export interface AccessTokenUpdate {
  token: string | null;
  source: AccessTokenUpdateSource;
}

let _accessToken: string | null = (() => {
  try {
    return typeof localStorage !== "undefined" ? localStorage.getItem(TOKEN_STORAGE_KEY) : null;
  } catch {
    return null;
  }
})();

const accessTokenListeners = new Set<(update: AccessTokenUpdate) => void>();

function notifyAccessTokenListeners(update: AccessTokenUpdate) {
  accessTokenListeners.forEach((listener) => {
    try {
      listener(update);
    } catch (error) {
      console.error("Failed to notify access token listener", error);
    }
  });
}

export function subscribeToAccessTokenUpdates(listener: (update: AccessTokenUpdate) => void): () => void {
  accessTokenListeners.add(listener);
  return () => {
    accessTokenListeners.delete(listener);
  };
}

export function setApiAccessToken(
  token: string | null,
  options: { source?: AccessTokenUpdateSource } = {},
) {
  _accessToken = token;
  try {
    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch {
    // localStorage unavailable (e.g. private browsing restrictions)
  }

  notifyAccessTokenListeners({ token, source: options.source ?? (token ? "manual" : "logout") });
}

export function getApiAccessToken(): string | null {
  return _accessToken;
}

function getAccessToken(): string | null {
  return _accessToken;
}

// --- Cross-tab account switch -------------------------------------------
//
// The session cookie and localStorage token are shared by every tab on the
// origin, but each tab keeps its own in-memory token. Once another tab signs
// in as a different account (or signs out), this tab's memory token and the
// cookie belong to different people: client fetches would act as one user
// while server-rendered pages and proxies act as the other. The watcher in
// lib/session/account-switch marks the tab here, and from then on every API
// request is refused until the page reloads under the new session.

export interface AccountSwitchInfo {
  /** Subject of the account that now owns the browser session; null = signed out. */
  subject: string | null;
  email?: string | null;
}

export class AccountSwitchedError extends Error {
  readonly info: AccountSwitchInfo;

  constructor(info: AccountSwitchInfo) {
    super(
      info.subject
        ? "The browser session now belongs to another account. Reload the page."
        : "You signed out in another tab. Reload the page.",
    );
    this.name = "AccountSwitchedError";
    this.info = info;
  }
}

export function isAccountSwitchedError(error: unknown): error is AccountSwitchedError {
  return error instanceof AccountSwitchedError;
}

let _accountSwitch: AccountSwitchInfo | null = null;
const accountSwitchListeners = new Set<(info: AccountSwitchInfo) => void>();

export function getAccountSwitch(): AccountSwitchInfo | null {
  return _accountSwitch;
}

/** Freeze this tab: subsequent API calls throw AccountSwitchedError. First call wins. */
export function markAccountSwitched(info: AccountSwitchInfo) {
  if (_accountSwitch) {
    return;
  }
  _accountSwitch = info;
  accountSwitchListeners.forEach((listener) => {
    try {
      listener(info);
    } catch (error) {
      console.error("Failed to notify account switch listener", error);
    }
  });
}

/** Test seam. A real tab only leaves the switched state by reloading. */
export function clearAccountSwitchForTests() {
  _accountSwitch = null;
}

export function subscribeToAccountSwitch(listener: (info: AccountSwitchInfo) => void): () => void {
  accountSwitchListeners.add(listener);
  return () => {
    accountSwitchListeners.delete(listener);
  };
}

const REQUEST_TIMEOUT_MS = 10_000;
// `{ error: "subscription_locked" }` on a 402 — see docs/plan/stripe-subscriptions.md.
const SUBSCRIPTION_LOCKED_ERROR_CODE = "subscription_locked";
// A file upload is bounded by the user's uplink, not by how fast the API
// answers. At 10s a 2 MB image — the size the upload UI advertises — is aborted
// part-way on any ordinary home connection, and the server sees a truncated body
// rather than a cancelled request.
const UPLOAD_TIMEOUT_MS = 120_000;

async function performRequest(path: string, init: RequestInit = {}) {
  if (_accountSwitch) {
    throw new AccountSwitchedError(_accountSwitch);
  }
  const headers = new Headers(init.headers);
  const isMultipartBody =
    typeof FormData !== "undefined" && init.body instanceof FormData;
  if (init.body && !headers.has("Content-Type") && !isMultipartBody) {
    headers.set("Content-Type", "application/json");
  }
  const token = getAccessToken();
  if (token && !headers.has("Authorization")) {
    // Always attach the token and let the server decide. The server's
    // TokenRefreshMiddleware proactively rotates tokens past half-life
    // via the X-Refreshed-Token header; a local pre-expiry throw would
    // short-circuit that refresh and log the user out instead.
    headers.set("Authorization", `Bearer ${token}`);
  }

  const controller = new AbortController();
  const userSignal = init.signal;

  if (userSignal) {
    if (userSignal.aborted) {
      controller.abort();
    } else {
      const forwardAbort = () => controller.abort();
      userSignal.addEventListener("abort", forwardAbort, { once: true });
    }
  }

  const timeout = setTimeout(
    () => controller.abort(),
    isMultipartBody ? UPLOAD_TIMEOUT_MS : REQUEST_TIMEOUT_MS,
  );

  let response: Response;
  try {
    response = await fetch(buildProxyPath(path), {
      cache: "no-store",
      redirect: "manual",
      ...init,
      headers,
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      throw new ApiError("Request timed out while contacting the Scirix API.", 0);
    }
    const message =
      error instanceof Error ? error.message : "Unable to reach the Scirix API.";
    throw new ApiError(message, 0);
  } finally {
    clearTimeout(timeout);
  }

  if (!response.ok) {
    if (response.status === 307) {
      throw new ApiError("Session expired. Please sign in again.", response.status);
    }
    const parsed = await parseError(response);
    if (response.status === 402 && parsed.code === SUBSCRIPTION_LOCKED_ERROR_CODE) {
      // The org's subscription is locked. Route to the full-screen lock gate
      // (components/billing/SubscriptionGate) instead of a generic error.
      markSubscriptionLocked();
    }
    throw new ApiError(parsed.message, response.status, { code: parsed.code, body: parsed.body });
  }

  applyRefreshedTokenFromResponse(response);

  return response;
}


const REFRESHED_TOKEN_HEADER = "X-Refreshed-Token";

export function applyRefreshedTokenFromResponse(response: Response) {
  const refreshedToken = response.headers.get(REFRESHED_TOKEN_HEADER);
  if (refreshedToken) {
    setApiAccessToken(refreshedToken, { source: "refresh" });
  }
}



async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await performRequest(path, init);

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  return text ? (JSON.parse(text) as T) : (undefined as T);
}

async function fetchJson<T>(path: string, init?: RequestInit) {
  return requestJson<T>(path, init);
}

/**
 * Authenticated JSON request against the Scirix API, shared with the feature
 * clients that live in their own modules (e.g. lib/billing-api-client.ts) so
 * they inherit the token, timeout, refresh and lock handling above.
 */
export async function apiRequestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  return requestJson<T>(path, init);
}

export async function pingSessionHeartbeat() {
  await fetchJson<unknown>("/api/auth/providers");
}

// --- Per-namespace feature overrides (org owners, org:manage) ---

// Resolved state of one product feature for a namespace: the org grant, this
// namespace's override (null = follow default), and the computed effective value.
export type NamespaceFeatureState = {
  key: string;
  name: string;
  description: string;
  dependsOn: string | null;
  experimental: boolean;
  permitted: boolean;
  defaultEnabled: boolean;
  override: boolean | null;
  effective: boolean;
};

export async function fetchNamespaceFeatures(namespaceId: string): Promise<NamespaceFeatureState[]> {
  return fetchJson<NamespaceFeatureState[]>(
    `/api/namespaces/${encodeURIComponent(namespaceId)}/features`,
  );
}

// Sets an explicit override for a feature in this namespace.
export async function setNamespaceFeatureOverride(
  namespaceId: string,
  featureKey: string,
  enabled: boolean,
): Promise<NamespaceFeatureState> {
  return fetchJson<NamespaceFeatureState>(
    `/api/namespaces/${encodeURIComponent(namespaceId)}/features/${encodeURIComponent(featureKey)}`,
    { method: "PUT", body: JSON.stringify({ enabled }) },
  );
}

// Clears the override, reverting the feature to the org default.
export async function clearNamespaceFeatureOverride(
  namespaceId: string,
  featureKey: string,
): Promise<NamespaceFeatureState> {
  return fetchJson<NamespaceFeatureState>(
    `/api/namespaces/${encodeURIComponent(namespaceId)}/features/${encodeURIComponent(featureKey)}`,
    { method: "DELETE" },
  );
}

function normalizeNamespaceSummary(raw: RawNamespaceSummary): NamespaceSummary {
  const namespaceId = raw.namespaceId ?? raw.namespace_id ?? raw.id ?? raw.name ?? "";
  const createdAt = raw.createdAt ?? raw.created_at ?? new Date().toISOString();
  const rawExternalDocumentation = raw.externalDocumentation ?? raw.external_documentation;

  return {
    namespaceId,
    name: raw.name ?? namespaceId,
    slug: raw.slug ?? "",
    description: raw.description ?? "",
    icon: raw.icon ?? "🛠️",
    createdAt,
    organizationId: raw.organizationId ?? raw.organization_id ?? null,
    organizationName: raw.organizationName ?? raw.organization_name ?? null,
    allowAgentToEdit: raw.allowAgentToEdit ?? raw.allow_agent_to_edit ?? false,
    agentState: raw.agentState ?? raw.agent_state ?? "Idle",
    // Absent on namespaces created before provisioning became asynchronous —
    // those already have their database, so Ready is the correct fallback.
    databaseState: raw.databaseState ?? raw.database_state ?? "Ready",
    currentlyActiveSessionId: raw.currentlyActiveSessionId ?? raw.currently_active_session_id ?? "",
    hasWritableRepository: raw.hasWritableRepository ?? raw.has_writable_repository ?? false,
    repositories: raw.repositories ?? [],
    rootDirectory: raw.rootDirectory ?? raw.root_directory ?? "",
    sourceDirectory: raw.sourceDirectory ?? raw.source_directory ?? "",
    autoDependencies: raw.autoDependencies ?? raw.auto_dependencies ?? null,
    autoReview: raw.autoReview ?? (raw.auto_review
      ? {
          enabled: raw.auto_review.enabled,
          treatAutoReviewedAsReviewed: raw.auto_review.treat_auto_reviewed_as_reviewed,
          maxAutoReviewAttempts: raw.auto_review.max_auto_review_attempts,
        }
      : null),
    branchAdoption: raw.branchAdoption ?? (raw.branch_adoption
      ? {
          enabled: raw.branch_adoption.enabled,
          maxBranchesPerCycle: raw.branch_adoption.max_branches_per_cycle,
          rules: (raw.branch_adoption.rules ?? []).map((r: { pattern: string; kind: string; description?: string | null }) => ({
            pattern: r.pattern,
            kind: r.kind,
            description: r.description ?? undefined,
          })),
        }
      : null),
    analysis: raw.analysis
      ? {
          ignoredPaths: (raw.analysis.ignored_paths ?? []).filter((p): p is string => typeof p === "string"),
          archivedPaths: (raw.analysis.archived_paths ?? []).filter((p): p is string => typeof p === "string"),
          maxFileSizeKb: typeof raw.analysis.max_file_size_kb === "number" ? raw.analysis.max_file_size_kb : null,
          governanceExtractionEnabled: raw.analysis.governance_extraction_enabled ?? true,
          triggers: {
            startup: raw.analysis.triggers?.startup ?? true,
            fileWrite: raw.analysis.triggers?.file_write ?? true,
            checkout: raw.analysis.triggers?.checkout ?? true,
            checkoutDebounceMinutes:
              typeof raw.analysis.triggers?.checkout_debounce_minutes === "number"
                ? raw.analysis.triggers.checkout_debounce_minutes
                : 10,
          },
          documentationPaths: raw.analysis.documentation_paths
            ? {
                projectPaths: (raw.analysis.documentation_paths.project_paths ?? [])
                  .filter((p: unknown): p is string => typeof p === "string"),
                userFacingPaths: (raw.analysis.documentation_paths.user_facing_paths ?? [])
                  .filter((p: unknown): p is string => typeof p === "string"),
                thirdPartyPaths: (raw.analysis.documentation_paths.third_party_paths ?? [])
                  .filter((p: unknown): p is string => typeof p === "string"),
              }
            : { projectPaths: [], userFacingPaths: [], thirdPartyPaths: [] },
          documentationClassPaths: (raw.analysis.documentation_class_paths ?? [])
            .map((r) => ({
              classSlug: typeof r?.class_slug === "string" ? r.class_slug : "",
              patterns: (r?.patterns ?? []).filter((p: unknown): p is string => typeof p === "string"),
            }))
            .filter((r) => r.classSlug.length > 0),
          // Server defaults mirrored client-side, so a namespace saved before
          // these fields existed renders as the server will actually behave.
          documentationClassLlmEnabled: raw.analysis.documentation_class_llm_enabled ?? false,
          documentationClassLlmMaxDocsPerRun:
            typeof raw.analysis.documentation_class_llm_max_docs_per_run === "number"
              ? raw.analysis.documentation_class_llm_max_docs_per_run
              : 200,
          infrastructureAsCodePaths: (raw.analysis.infrastructure_as_code_paths ?? [])
            .filter((p: unknown): p is string => typeof p === "string"),
          configurationPaths: (raw.analysis.configuration_paths ?? [])
            .filter((p: unknown): p is string => typeof p === "string"),
          maxDocsCrawlDepth:
            typeof raw.analysis.max_docs_crawl_depth === "number"
              ? raw.analysis.max_docs_crawl_depth
              : null,
          // Defaults mirror the server's, so a namespace saved before these
          // fields existed renders as the server will actually behave rather
          // than as "off".
          libraryCatalogEnabled: raw.analysis.library_catalog_enabled ?? true,
          libraryDocsEnabled: raw.analysis.library_docs_enabled ?? true,
          libraryDocsMaxPagesPerRun:
            typeof raw.analysis.library_docs_max_pages_per_run === "number"
              ? raw.analysis.library_docs_max_pages_per_run
              : 50,
          libraryDocsMaxPagesPerLibrary:
            typeof raw.analysis.library_docs_max_pages_per_library === "number"
              ? raw.analysis.library_docs_max_pages_per_library
              : 5,
          libraryDocsIncludeTransitive: raw.analysis.library_docs_include_transitive ?? false,
          libraryCMakeLlmFallbackEnabled: raw.analysis.library_cmake_llm_fallback_enabled ?? false,
          libraryOutdatedCheckEnabled: raw.analysis.library_outdated_check_enabled ?? false,
          libraryOutdatedCheckCron:
            typeof raw.analysis.library_outdated_check_cron === "string"
              ? raw.analysis.library_outdated_check_cron
              : "0 6 * * 1",
          libraryAdvisoryCheckEnabled: raw.analysis.library_advisory_check_enabled ?? false,
          libraryAdvisoryCheckCron:
            typeof raw.analysis.library_advisory_check_cron === "string"
              ? raw.analysis.library_advisory_check_cron
              : "0 5 * * *",
          // Data-journey passes default OFF on the server; mirror that here.
          dataEntityGraphEnabled: raw.analysis.data_entity_graph_enabled ?? false,
          routeExtractionEnabled: raw.analysis.route_extraction_enabled ?? false,
          eventFlowEnabled: raw.analysis.event_flow_enabled ?? false,
          edgeInferenceEnabled: raw.analysis.edge_inference_enabled ?? false,
          // Reading API documents defaults ON on the server, AI link suggestions and AI reading OFF.
          apiSpecExtractionEnabled: raw.analysis.api_spec_extraction_enabled ?? true,
          integrationAiLinksEnabled: raw.analysis.integration_ai_links_enabled ?? false,
          integrationProseExtractionEnabled: raw.analysis.integration_prose_extraction_enabled ?? false,
          // The governance analysis stage and both its analyzers default ON on the server.
          governanceAnalysisAfterIndexing: raw.analysis.governance_analysis_after_indexing ?? true,
          aiSystemDetectionEnabled: raw.analysis.ai_system_detection_enabled ?? true,
          ownershipAnalysisEnabled: raw.analysis.ownership_analysis_enabled ?? true,
        }
      : null,
    vcsCommitNotifications: raw.vcsCommitNotifications ?? (raw.vcs_commit_notifications
      ? { enabled: Boolean(raw.vcs_commit_notifications.enabled) }
      : null),
    changeDelivery: raw.changeDelivery ?? (raw.change_delivery
      ? {
          mode: raw.change_delivery.mode ?? "DirectMerge",
          draftUntilApproved: raw.change_delivery.draft_until_approved ?? false,
          autoMergeApprovedPrs: raw.change_delivery.auto_merge_approved_prs ?? false,
          targetBranch: raw.change_delivery.target_branch ?? null,
        }
      : null),
    externalDocumentation: rawExternalDocumentation
      ? {
          allowAgentWrites: rawExternalDocumentation.allow_agent_writes ?? true,
          writeLogRetentionDays:
            typeof rawExternalDocumentation.write_log_retention_days === "number"
              ? rawExternalDocumentation.write_log_retention_days
              : null,
        }
      : null,
  };
}

interface RawCronJobDocument {
  id?: string;
  namespace_id?: string;
  namespaceId?: string;
  name?: string;
  cron_expression?: string;
  cronExpression?: string;
  job_kind?: string | null;
  jobKind?: string | null;
  system_task?: string | null;
  systemTask?: string | null;
  config?: Record<string, unknown> | null;
  instructions?: string;
  indefinite?: boolean;
  status?: string;
  last_run_at?: string | null;
  lastRunAt?: string | null;
  next_run_at?: string | null;
  nextRunAt?: string | null;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
  created_by_kind?: string | null;
  createdByKind?: string | null;
  created_by?: string | null;
  createdBy?: string | null;
  created_by_display_name?: string | null;
  createdByDisplayName?: string | null;
  approved_by?: string | null;
  approvedBy?: string | null;
  approved_by_display_name?: string | null;
  approvedByDisplayName?: string | null;
  approved_at?: string | null;
  approvedAt?: string | null;
}

const KNOWN_CRON_JOB_STATUSES: CronJobStatus[] = [
  "active",
  "paused",
  "pending",
  "completed",
];

function normalizeCronJobStatus(value?: string | null): CronJobStatus {
  if (!value) {
    return "active";
  }

  const normalized = value.toLowerCase();
  return (KNOWN_CRON_JOB_STATUSES.includes(normalized as CronJobStatus)
    ? normalized
    : "active") as CronJobStatus;
}

function normalizeCronJobDocument(raw: RawCronJobDocument, fallbackNamespaceId?: NamespaceId): CronJob {
  const namespaceId = raw.namespaceId ?? raw.namespace_id ?? fallbackNamespaceId ?? "";
  const createdAt = raw.createdAt ?? raw.created_at ?? new Date().toISOString();
  const updatedAt = raw.updatedAt ?? raw.updated_at ?? createdAt;
  const createdByKind = normalizeCronJobCreatorKind(raw.createdByKind ?? raw.created_by_kind);

  return {
    id: raw.id ?? "",
    namespaceId,
    name: raw.name ?? "",
    cronExpression: raw.cronExpression ?? raw.cron_expression ?? "",
    // Left undefined when absent rather than defaulted here, so a legacy row is
    // distinguishable from an explicit "agent" — resolveCronJobKind owns the
    // fallback in one place.
    jobKind: raw.jobKind ?? raw.job_kind ?? undefined,
    systemTask: raw.systemTask ?? raw.system_task ?? null,
    config: raw.config ?? null,
    instructions: raw.instructions ?? "",
    indefinite: Boolean(raw.indefinite),
    status: normalizeCronJobStatus(raw.status),
    lastRunAt: raw.lastRunAt ?? raw.last_run_at ?? null,
    nextRunAt: raw.nextRunAt ?? raw.next_run_at ?? null,
     createdBy: raw.createdBy ?? raw.created_by ?? null,
     createdByDisplayName: raw.createdByDisplayName ?? raw.created_by_display_name ?? null,
     createdByKind,
     approvedBy: raw.approvedBy ?? raw.approved_by ?? null,
     approvedByDisplayName: raw.approvedByDisplayName ?? raw.approved_by_display_name ?? null,
     approvedAt: raw.approvedAt ?? raw.approved_at ?? null,
    createdAt,
    updatedAt,
  };
}

interface RawCronJobRunDocument {
  id?: string;
  job_id?: string;
  jobId?: string;
  job_name?: string;
  jobName?: string;
  namespace_id?: string;
  namespaceId?: string;
  queued_at?: string | null;
  queuedAt?: string | null;
  started_at?: string;
  startedAt?: string;
  completed_at?: string | null;
  completedAt?: string | null;
  duration_ms?: number | null;
  durationMs?: number | null;
  result?: string;
  summary?: string | null;
  task_id?: string | null;
  taskId?: string | null;
  task_number?: number | null;
  taskNumber?: number | null;
  logs_preview?: string | null;
  logsPreview?: string | null;
  action_log?: string[] | null;
  actionLog?: string[] | null;
  prompt?: string | null;
  detail?: string | null;
  error?: string | null;
}

const KNOWN_CRON_JOB_RUN_RESULTS: CronJobRunResult[] = [
  "success",
  "failed",
  "partial",
  "running",
  "queued",
];

function normalizeCronJobRunResult(value?: string | null): CronJobRunResult {
  if (!value) {
    return "queued";
  }

  const normalized = value.toLowerCase();
  return (KNOWN_CRON_JOB_RUN_RESULTS.includes(normalized as CronJobRunResult)
    ? normalized
    : "queued") as CronJobRunResult;
}

function normalizeCronJobCreatorKind(value?: string | null): CronJobCreatorKind {
  if (!value) {
    return "user";
  }

  const normalized = value.trim().toLowerCase();
  if (normalized === "agent" || normalized === "user" || normalized === "system") {
    return normalized;
  }

  return value.trim() as CronJobCreatorKind;
}

function normalizeCronJobRun(raw: RawCronJobRunDocument, fallbackNamespaceId?: NamespaceId): CronJobRun {
  const namespaceId = raw.namespaceId ?? raw.namespace_id ?? fallbackNamespaceId ?? "";
  const startedAt = raw.startedAt ?? raw.started_at ?? new Date().toISOString();

  return {
    id: raw.id ?? "",
    jobId: raw.jobId ?? raw.job_id ?? "",
    jobName: raw.jobName ?? raw.job_name ?? "",
    namespaceId,
    queuedAt: raw.queuedAt ?? raw.queued_at ?? null,
    startedAt,
    completedAt: raw.completedAt ?? raw.completed_at ?? null,
    durationMs: raw.durationMs ?? raw.duration_ms ?? null,
    result: normalizeCronJobRunResult(raw.result),
    summary: raw.summary ?? null,
    taskId: raw.taskId ?? raw.task_id ?? null,
    taskNumber: raw.taskNumber ?? raw.task_number ?? null,
    logsPreview: raw.logsPreview ?? raw.logs_preview ?? null,
    actionLog: raw.actionLog ?? raw.action_log ?? null,
    prompt: raw.prompt ?? null,
    detail: raw.detail ?? null,
    error: raw.error ?? null,
  };
}

interface RawCronJobRunListResponse {
  namespace_id?: string;
  namespaceId?: string;
  runs?: RawCronJobRunDocument[];
  next_cursor?: string | null;
  nextCursor?: string | null;
}

function normalizeCronJobRunListResponse(
  raw: RawCronJobRunListResponse | null | undefined,
  fallbackNamespaceId: NamespaceId,
): CronJobRunListResponse {
  if (!raw) {
    return {
      namespaceId: fallbackNamespaceId,
      runs: [],
      nextCursor: null,
    };
  }

  return {
    namespaceId: raw.namespaceId ?? raw.namespace_id ?? fallbackNamespaceId,
    runs: (raw.runs ?? []).map((run) => normalizeCronJobRun(run, fallbackNamespaceId)),
    nextCursor: raw.nextCursor ?? raw.next_cursor ?? null,
  };
}

export async function fetchLastViewedNamespace(): Promise<string | null> {
  const result = await fetchJson<{ namespaceId: string } | undefined>(
    "/api/me/last-viewed-namespace",
  );
  return result?.namespaceId ?? null;
}

export async function putLastViewedNamespace(namespaceId: string): Promise<void> {
  await fetchJson<void>("/api/me/last-viewed-namespace", {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ namespaceId }),
  });
}

export async function fetchNamespaces() {
  const namespaces = await fetchJson<RawNamespaceSummary[]>("/api/namespaces");
  return (namespaces ?? []).map(normalizeNamespaceSummary).filter((namespace) => namespace.namespaceId.length > 0);
}

export async function fetchNamespaceById(namespaceId: NamespaceId) {
  const normalizedId = namespaceId?.trim();
  if (!normalizedId) {
    throw new ApiError("Namespace ID is required.", 400);
  }

  const response = await fetchJson<RawNamespaceSummary>(`/api/namespaces/${encodeURIComponent(normalizedId)}`);
  if (!response) {
    throw new ApiError("Namespace could not be found.", 404);
  }

  return normalizeNamespaceSummary(response);
}

export async function fetchNamespaceCronJobs(namespaceId: NamespaceId) {
  const normalizedId = namespaceId?.trim();
  if (!normalizedId) {
    throw new ApiError("Namespace ID is required to load cron jobs.", 400);
  }

  const response = await fetchJson<RawCronJobDocument[]>(
    `/api/namespaces/${encodeURIComponent(normalizedId)}/cron-jobs`,
  );

  return (response ?? []).map((doc) => normalizeCronJobDocument(doc, normalizedId));
}

/**
 * The job kinds this deployment can run, and the system tasks registered in it.
 * Fetched rather than hardcoded so a newly registered handler shows up in the
 * creation form with no frontend change.
 */
export async function fetchNamespaceCronJobKinds(namespaceId: NamespaceId) {
  const normalizedId = namespaceId?.trim();
  if (!normalizedId) {
    throw new ApiError("Namespace ID is required to load cron job kinds.", 400);
  }

  const response = await requestJson<CronJobKindDescriptor[]>(
    `/api/namespaces/${encodeURIComponent(normalizedId)}/cron-jobs/kinds`,
  );
  return response ?? [];
}

export async function createNamespaceCronJob(
  namespaceId: NamespaceId,
  payload: CreateCronJobPayload,
) {
  const normalizedId = namespaceId?.trim();
  if (!normalizedId) {
    throw new ApiError("Namespace ID is required to create a cron job.", 400);
  }

  const name = payload?.name?.trim();
  if (!name) {
    throw new ApiError("Cron jobs require a name.", 400);
  }

  const cronExpression = payload?.cronExpression?.trim();
  if (!cronExpression) {
    throw new ApiError("Provide a cron expression before saving the job.", 400);
  }

  const jobKind = payload.jobKind?.trim().toLowerCase() || "agent";
  const isSystem = jobKind === "system";

  const instructions = payload?.instructions?.trim() ?? "";
  // Only agent jobs have a prompt. Requiring instructions for a system job
  // would make it uncreatable; sending them would be rejected server-side.
  if (!isSystem && !instructions) {
    throw new ApiError("Describe what the agent should do in the cron job instructions.", 400);
  }

  const systemTask = payload.systemTask?.trim();
  if (isSystem && !systemTask) {
    throw new ApiError("Choose which system task this job should run.", 400);
  }

  let status: CronJobStatus | undefined;
  if (payload.status) {
    const normalizedStatus = payload.status.trim().toLowerCase();
    if (normalizedStatus === "active" || normalizedStatus === "paused" || normalizedStatus === "pending") {
      status = normalizedStatus as CronJobStatus;
    }
  }

  const body: CreateCronJobPayload = {
    name,
    cronExpression,
    instructions: isSystem ? "" : instructions,
    // System jobs are always indefinite — nothing can mark a mechanical task
    // complete, so a finite one would sit Active forever.
    indefinite: isSystem ? true : payload.indefinite ?? true,
    status,
    jobKind,
    systemTask: isSystem ? systemTask : undefined,
    config: isSystem ? payload.config ?? undefined : undefined,
  };

  const response = await requestJson<RawCronJobDocument>(
    `/api/namespaces/${encodeURIComponent(normalizedId)}/cron-jobs`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );

  return normalizeCronJobDocument(response, normalizedId);
}

export async function updateNamespaceCronJob(
  namespaceId: NamespaceId,
  cronJobId: string,
  payload: UpdateCronJobPayload,
) {
  const normalizedNamespaceId = namespaceId?.trim();
  if (!normalizedNamespaceId) {
    throw new ApiError("Namespace ID is required to update a cron job.", 400);
  }

  const normalizedCronJobId = cronJobId?.trim();
  if (!normalizedCronJobId) {
    throw new ApiError("Cron job ID is required for this update.", 400);
  }

  const body: Record<string, unknown> = {};
  if (payload.name !== undefined) {
    body.name = payload.name?.trim();
  }
  if (payload.cronExpression !== undefined) {
    body.cronExpression = payload.cronExpression?.trim();
  }
  if (payload.instructions !== undefined) {
    body.instructions = payload.instructions?.trim();
  }
  if (payload.indefinite !== undefined) {
    body.indefinite = payload.indefinite;
  }
  if (payload.status !== undefined) {
    body.status = payload.status;
  }
  if (payload.config !== undefined) {
    body.config = payload.config;
  }

  if (Object.keys(body).length === 0) {
    throw new ApiError("Select a cron job field to update before saving.", 400);
  }

  const response = await requestJson<RawCronJobDocument>(
    `/api/namespaces/${encodeURIComponent(normalizedNamespaceId)}/cron-jobs/${encodeURIComponent(normalizedCronJobId)}`,
    {
      method: "PUT",
      body: JSON.stringify(body),
    },
  );

  return normalizeCronJobDocument(response, normalizedNamespaceId);
}

export async function deleteNamespaceCronJob(namespaceId: NamespaceId, cronJobId: string) {
  const normalizedNamespaceId = namespaceId?.trim();
  if (!normalizedNamespaceId) {
    throw new ApiError("Namespace ID is required to delete a cron job.", 400);
  }

  const normalizedCronJobId = cronJobId?.trim();
  if (!normalizedCronJobId) {
    throw new ApiError("Cron job ID is required to delete a job.", 400);
  }

  await requestJson<void>(
    `/api/namespaces/${encodeURIComponent(normalizedNamespaceId)}/cron-jobs/${encodeURIComponent(normalizedCronJobId)}`,
    { method: "DELETE" },
  );
}

export async function completeNamespaceCronJob(namespaceId: NamespaceId, cronJobId: string) {
  const normalizedNamespaceId = namespaceId?.trim();
  if (!normalizedNamespaceId) {
    throw new ApiError("Namespace ID is required to complete a cron job.", 400);
  }

  const normalizedCronJobId = cronJobId?.trim();
  if (!normalizedCronJobId) {
    throw new ApiError("Cron job ID is required to complete a job.", 400);
  }

  const response = await requestJson<RawCronJobDocument>(
    `/api/namespaces/${encodeURIComponent(normalizedNamespaceId)}/cron-jobs/${encodeURIComponent(normalizedCronJobId)}/complete`,
    { method: "POST" },
  );

  return normalizeCronJobDocument(response, normalizedNamespaceId);
}

export async function runNamespaceCronJob(namespaceId: NamespaceId, cronJobId: string) {
  const normalizedNamespaceId = namespaceId?.trim();
  if (!normalizedNamespaceId) {
    throw new ApiError("Namespace ID is required to run a cron job.", 400);
  }

  const normalizedCronJobId = cronJobId?.trim();
  if (!normalizedCronJobId) {
    throw new ApiError("Cron job ID is required to run a job.", 400);
  }

  const response = await requestJson<RawCronJobDocument>(
    `/api/namespaces/${encodeURIComponent(normalizedNamespaceId)}/cron-jobs/${encodeURIComponent(normalizedCronJobId)}/run`,
    { method: "POST" },
  );

  return normalizeCronJobDocument(response, normalizedNamespaceId);
}

export async function fetchCronJobRuns(namespaceId: NamespaceId, query?: CronJobRunListQuery) {
  const normalizedNamespaceId = namespaceId?.trim();
  if (!normalizedNamespaceId) {
    throw new ApiError("Namespace ID is required to load cron job history.", 400);
  }

  const params = new URLSearchParams();
  const jobId = query?.jobId?.trim();
  if (jobId) {
    params.set("jobId", jobId);
  }

  const result = query?.result?.trim();
  if (result && result !== "all") {
    params.set("result", result);
  }

  const cursor = query?.cursor?.trim();
  if (cursor) {
    params.set("cursor", cursor);
  }

  if (typeof query?.limit === "number" && Number.isFinite(query.limit)) {
    const clampedLimit = Math.min(Math.max(Math.trunc(query.limit), 1), 100);
    params.set("limit", clampedLimit.toString());
  }

  const queryString = params.toString();
  const path = queryString
    ? `/api/namespaces/${encodeURIComponent(normalizedNamespaceId)}/cron-jobs/runs?${queryString}`
    : `/api/namespaces/${encodeURIComponent(normalizedNamespaceId)}/cron-jobs/runs`;

  const response = await fetchJson<RawCronJobRunListResponse>(path);
  return normalizeCronJobRunListResponse(response ?? null, normalizedNamespaceId);
}

export async function updateNamespaceSettings(
  namespaceId: NamespaceId,
  payload: UpdateNamespaceSettingsPayload,
) {
  const normalizedId = namespaceId?.trim();
  if (!normalizedId) {
    throw new ApiError("Namespace ID is required.", 400);
  }

  const body: Record<string, unknown> = {};
  if (payload.allowAgentToEdit !== undefined) {
    body.allowAgentToEdit = payload.allowAgentToEdit;
  }
  if (payload.agentState) {
    body.agentState = payload.agentState;
  }
  if (payload.name !== undefined) {
    body.name = payload.name;
  }
  if (payload.description !== undefined) {
    body.description = payload.description;
  }
  if (payload.icon !== undefined) {
    body.icon = payload.icon;
  }
  if (payload.autoDependencies !== undefined) {
    body.autoDependencies = payload.autoDependencies;
  }
  if (payload.autoReview !== undefined) {
    body.autoReview = {
      enabled: payload.autoReview.enabled,
      treat_auto_reviewed_as_reviewed: payload.autoReview.treatAutoReviewedAsReviewed,
      max_auto_review_attempts: payload.autoReview.maxAutoReviewAttempts,
    };
  }
  if (payload.branchAdoption !== undefined) {
    body.branchAdoption = {
      enabled: payload.branchAdoption.enabled,
      max_branches_per_cycle: payload.branchAdoption.maxBranchesPerCycle,
      rules: payload.branchAdoption.rules,
    };
  }
  if (payload.analysis !== undefined) {
    const analysisBody: Record<string, unknown> = {
      ignored_paths: payload.analysis.ignoredPaths,
      archived_paths: payload.analysis.archivedPaths,
      max_file_size_kb: payload.analysis.maxFileSizeKb,
      governance_extraction_enabled: payload.analysis.governanceExtractionEnabled,
      triggers: {
        startup: payload.analysis.triggers.startup,
        file_write: payload.analysis.triggers.fileWrite,
        checkout: payload.analysis.triggers.checkout,
        checkout_debounce_minutes: payload.analysis.triggers.checkoutDebounceMinutes,
      },
    };
    if (payload.analysis.documentationPaths !== undefined) {
      analysisBody.documentation_paths = {
        project_paths: payload.analysis.documentationPaths.projectPaths,
        user_facing_paths: payload.analysis.documentationPaths.userFacingPaths,
        third_party_paths: payload.analysis.documentationPaths.thirdPartyPaths,
      };
    }
    if (payload.analysis.documentationClassPaths !== undefined) {
      analysisBody.documentation_class_paths = payload.analysis.documentationClassPaths.map((r) => ({
        class_slug: r.classSlug,
        patterns: r.patterns,
      }));
    }
    if (payload.analysis.infrastructureAsCodePaths !== undefined) {
      analysisBody.infrastructure_as_code_paths = payload.analysis.infrastructureAsCodePaths;
    }
    if (payload.analysis.documentationClassLlmEnabled !== undefined) {
      analysisBody.documentation_class_llm_enabled = payload.analysis.documentationClassLlmEnabled;
    }
    if (payload.analysis.documentationClassLlmMaxDocsPerRun !== undefined) {
      analysisBody.documentation_class_llm_max_docs_per_run =
        payload.analysis.documentationClassLlmMaxDocsPerRun;
    }
    if (payload.analysis.configurationPaths !== undefined) {
      analysisBody.configuration_paths = payload.analysis.configurationPaths;
    }
    if (payload.analysis.maxDocsCrawlDepth !== undefined) {
      analysisBody.max_docs_crawl_depth = payload.analysis.maxDocsCrawlDepth;
    }
    // Each sent only when present. The server applies `analysis` as a partial
    // update over the stored settings, so a key left out here — including every
    // key this payload type has no field for — keeps its stored value.
    if (payload.analysis.libraryCatalogEnabled !== undefined) {
      analysisBody.library_catalog_enabled = payload.analysis.libraryCatalogEnabled;
    }
    if (payload.analysis.libraryDocsEnabled !== undefined) {
      analysisBody.library_docs_enabled = payload.analysis.libraryDocsEnabled;
    }
    if (payload.analysis.libraryDocsMaxPagesPerRun !== undefined) {
      analysisBody.library_docs_max_pages_per_run = payload.analysis.libraryDocsMaxPagesPerRun;
    }
    if (payload.analysis.libraryDocsMaxPagesPerLibrary !== undefined) {
      analysisBody.library_docs_max_pages_per_library = payload.analysis.libraryDocsMaxPagesPerLibrary;
    }
    if (payload.analysis.libraryDocsIncludeTransitive !== undefined) {
      analysisBody.library_docs_include_transitive = payload.analysis.libraryDocsIncludeTransitive;
    }
    if (payload.analysis.libraryCMakeLlmFallbackEnabled !== undefined) {
      analysisBody.library_cmake_llm_fallback_enabled = payload.analysis.libraryCMakeLlmFallbackEnabled;
    }
    if (payload.analysis.libraryOutdatedCheckEnabled !== undefined) {
      analysisBody.library_outdated_check_enabled = payload.analysis.libraryOutdatedCheckEnabled;
    }
    if (payload.analysis.libraryOutdatedCheckCron !== undefined) {
      analysisBody.library_outdated_check_cron = payload.analysis.libraryOutdatedCheckCron;
    }
    if (payload.analysis.libraryAdvisoryCheckEnabled !== undefined) {
      analysisBody.library_advisory_check_enabled = payload.analysis.libraryAdvisoryCheckEnabled;
    }
    if (payload.analysis.libraryAdvisoryCheckCron !== undefined) {
      analysisBody.library_advisory_check_cron = payload.analysis.libraryAdvisoryCheckCron;
    }
    if (payload.analysis.dataEntityGraphEnabled !== undefined) {
      analysisBody.data_entity_graph_enabled = payload.analysis.dataEntityGraphEnabled;
    }
    if (payload.analysis.routeExtractionEnabled !== undefined) {
      analysisBody.route_extraction_enabled = payload.analysis.routeExtractionEnabled;
    }
    if (payload.analysis.eventFlowEnabled !== undefined) {
      analysisBody.event_flow_enabled = payload.analysis.eventFlowEnabled;
    }
    if (payload.analysis.edgeInferenceEnabled !== undefined) {
      analysisBody.edge_inference_enabled = payload.analysis.edgeInferenceEnabled;
    }
    if (payload.analysis.apiSpecExtractionEnabled !== undefined) {
      analysisBody.api_spec_extraction_enabled = payload.analysis.apiSpecExtractionEnabled;
    }
    if (payload.analysis.integrationAiLinksEnabled !== undefined) {
      analysisBody.integration_ai_links_enabled = payload.analysis.integrationAiLinksEnabled;
    }
    if (payload.analysis.integrationProseExtractionEnabled !== undefined) {
      analysisBody.integration_prose_extraction_enabled = payload.analysis.integrationProseExtractionEnabled;
    }
    if (payload.analysis.governanceAnalysisAfterIndexing !== undefined) {
      analysisBody.governance_analysis_after_indexing = payload.analysis.governanceAnalysisAfterIndexing;
    }
    if (payload.analysis.aiSystemDetectionEnabled !== undefined) {
      analysisBody.ai_system_detection_enabled = payload.analysis.aiSystemDetectionEnabled;
    }
    if (payload.analysis.ownershipAnalysisEnabled !== undefined) {
      analysisBody.ownership_analysis_enabled = payload.analysis.ownershipAnalysisEnabled;
    }
    body.analysis = analysisBody;
  }
  if (payload.vcsCommitNotifications !== undefined) {
    body.vcsCommitNotifications = { enabled: payload.vcsCommitNotifications.enabled };
  }
  if (payload.changeDelivery !== undefined) {
    body.changeDelivery = {
      mode: payload.changeDelivery.mode,
      draft_until_approved: payload.changeDelivery.draftUntilApproved,
      auto_merge_approved_prs: payload.changeDelivery.autoMergeApprovedPrs,
      target_branch: payload.changeDelivery.targetBranch,
    };
  }
  if (payload.externalDocumentation !== undefined) {
    body.externalDocumentation = {
      allow_agent_writes: payload.externalDocumentation.allowAgentWrites,
      write_log_retention_days: payload.externalDocumentation.writeLogRetentionDays,
    };
  }

  if (Object.keys(body).length === 0) {
    throw new ApiError("Select a setting to update before saving.", 400);
  }

  const response = await requestJson<RawNamespaceSummary>(
    `/api/namespaces/${encodeURIComponent(normalizedId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    },
  );

  return normalizeNamespaceSummary(response);
}

/**
 * Switches on the three static data-journey passes (data-entity graph, route
 * index, event flow) and leaves every other analysis setting exactly as
 * stored. Edge inference stays as it is: it spends tokens, so it is only
 * ever switched on in Settings.
 */
export async function enableDataJourneyPasses(namespaceId: NamespaceId): Promise<NamespaceSummary> {
  const normalizedId = namespaceId?.trim();
  if (!normalizedId) {
    throw new ApiError("Namespace ID is required.", 400);
  }
  const path = `/api/namespaces/${encodeURIComponent(normalizedId)}`;

  const stored = await fetchJson<{ analysis?: Record<string, unknown> | null }>(path);
  if (!stored) {
    throw new ApiError("Namespace could not be found.", 404);
  }
  const analysis = {
    ...(stored.analysis ?? {}),
    data_entity_graph_enabled: true,
    route_extraction_enabled: true,
    event_flow_enabled: true,
  };

  const response = await requestJson<RawNamespaceSummary>(path, {
    method: "PATCH",
    body: JSON.stringify({ analysis }),
  });
  return normalizeNamespaceSummary(response);
}

// Matches WorkspaceTaskQueryOptions.DefaultPageSize on the API.
export const TASK_LIST_DEFAULT_PAGE_SIZE = 25;

export async function fetchNamespaceTasks(
  namespaceId: NamespaceId,
  query?: NamespaceTaskListQuery,
): Promise<NamespaceTasksResponse> {
  const params = new URLSearchParams();
  const normalizedState = query?.state?.trim();
  if (normalizedState) {
    params.set("state", normalizedState);
  }

  const normalizedSort = query?.sort?.trim();
  if (normalizedSort) {
    params.set("sort", normalizedSort);
  }

  const normalizedSearch = query?.search?.trim();
  if (normalizedSearch) {
    params.set("search", normalizedSearch);
  }

  if (query?.labels?.length) {
    query.labels
      .map((label) => label?.trim())
      .filter((label): label is string => Boolean(label))
      .forEach((label) => params.append("labels", label));
  }

  const createdFrom = query?.createdFrom?.trim();
  if (createdFrom) {
    params.set("createdFrom", createdFrom);
  }

  const createdTo = query?.createdTo?.trim();
  if (createdTo) {
    params.set("createdTo", createdTo);
  }

  const page = Math.max(1, query?.page ?? 1);
  const pageSize = query?.pageSize ?? TASK_LIST_DEFAULT_PAGE_SIZE;
  params.set("page", String(page));
  params.set("pageSize", String(pageSize));

  const response = await fetchJson<NamespaceTasksResponse>(
    `/api/namespaces/${namespaceId}/tasks?${params.toString()}`,
  );

  return {
    namespaceId: response.namespaceId ?? namespaceId,
    tasks: response.tasks ?? [],
    total: response.total ?? response.tasks?.length ?? 0,
    page: response.page ?? page,
    pageSize: response.pageSize ?? pageSize,
  };
}

export async function fetchNamespaceTask(
  namespaceId: NamespaceId,
  taskNumber: number,
) {
  if (!Number.isFinite(taskNumber)) {
    throw new ApiError("A numeric task identifier is required.", 400);
  }
  const url = `/api/namespaces/${namespaceId}/tasks/${taskNumber}`;
  return fetchJson<TaskSummary>(url);
}

export async function fetchTaskEvents(
  namespaceId: string,
  taskNumber: number,
  since?: string,
  signal?: AbortSignal,
): Promise<unknown[]> {
  // Use performRequest so the JWT bearer token is attached. Raw fetch with
  // `credentials: "include"` is cookie-only and the API only honours bearer
  // auth, which produced 401s on every seed call.
  const path = buildTaskEventsPath(namespaceId, taskNumber, since);
  const response = await performRequest(path, { signal });
  if (!response.ok) {
    throw new ApiError(`fetchTaskEvents failed: ${response.status}`, response.status);
  }
  return (await response.json()) as unknown[];
}

/**
 * EventSource cannot attach an Authorization header, so the API's
 * JwtBearerEvents.OnMessageReceived hook in Program.cs accepts the JWT as an
 * `access_token` query param (same mechanism documented for buildSlackInstallUrl
 * and the Instant Assistant stream).
 */
export function buildTaskEventStreamUrl(
  namespaceId: string,
  taskNumber: number,
  since?: string,
): string {
  const base = buildAgentApiUrl(
    `/api/namespaces/${namespaceId}/tasks/${taskNumber}/events/stream`,
  );
  const params = new URLSearchParams();
  if (since) params.set("since", since);
  const token = getApiAccessToken();
  if (token) params.set("access_token", token);
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

function buildTaskEventsPath(
  namespaceId: string,
  taskNumber: number,
  since?: string,
): string {
  const params = new URLSearchParams({ limit: "500" });
  if (since) params.set("since", since);
  return `/api/namespaces/${namespaceId}/tasks/${taskNumber}/events?${params.toString()}`;
}

export async function searchWorkspaceKnowledge(
  namespaceId: NamespaceId,
  payload: WorkspaceSemanticSearchPayload,
): Promise<WorkspaceSearchResponse> {
  const normalizedNamespace = namespaceId?.trim();
  if (!normalizedNamespace) {
    throw new ApiError("A namespace is required to run semantic search.", 400);
  }

  const normalizedQuery = payload.query?.trim();
  if (!normalizedQuery) {
    throw new ApiError("Describe what you're looking for before running semantic search.", 400);
  }

  const limit = Math.min(Math.max(payload.limit ?? 8, 1), 50);
  const url = `/api/namespaces/${normalizedNamespace}/search`;
  return requestJson<WorkspaceSearchResponse>(url, {
    method: "POST",
    body: JSON.stringify({ query: normalizedQuery, limit }),
    signal: payload.signal,
  });
}

export type WorkspaceTaskCommentPayload = {
  body: string;
  attachments?: File[];
  signal?: AbortSignal;
};

export async function createWorkspaceTaskComment(
  namespaceId: NamespaceId,
  taskId: string,
  payload: WorkspaceTaskCommentPayload,
) {
  const url = `/api/namespaces/${namespaceId}/tasks/${taskId}/comments`;
  const bodyText = payload.body?.trim() ?? "";
  if (!bodyText) {
    throw new ApiError("A comment body is required.", 400);
  }

  const attachments = payload.attachments?.filter(Boolean) ?? [];
  const hasAttachments = attachments.length > 0;

  if (hasAttachments) {
    const form = new FormData();
    form.append("body", bodyText);
    for (const file of attachments) {
      form.append("files", file, file.name);
    }

    return requestJson<TaskThreadEventDto>(url, {
      method: "POST",
      body: form,
      signal: payload.signal,
    });
  }

  return requestJson<TaskThreadEventDto>(url, {
    method: "POST",
    body: JSON.stringify({ body: bodyText }),
    signal: payload.signal,
  });
}

export async function createNamespaceTask(
  namespaceId: NamespaceId,
  payload: TaskCreatePayload,
) {
  const url = `/api/namespaces/${namespaceId}/tasks`;
  return requestJson<TaskSummary>(url, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateNamespaceTask(
  namespaceId: NamespaceId,
  taskNumber: number,
  payload: TaskUpdatePayload,
) {
  if (!Number.isFinite(taskNumber)) {
    throw new ApiError("A numeric task identifier is required.", 400);
  }

  const url = `/api/namespaces/${namespaceId}/tasks/${taskNumber}`;
  return requestJson<TaskSummary>(url, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteNamespaceTask(namespaceId: NamespaceId, taskNumber: number) {
  if (!Number.isFinite(taskNumber)) {
    throw new ApiError("A numeric task identifier is required.", 400);
  }

  const url = `/api/namespaces/${namespaceId}/tasks/${taskNumber}`;
  await requestJson<void>(url, { method: "DELETE" });
}

export async function closeNamespaceTask(
  namespaceId: NamespaceId,
  taskNumber: number,
  payload?: TaskClosePayload,
) {
  if (!Number.isFinite(taskNumber)) {
    throw new ApiError("A numeric task identifier is required.", 400);
  }

  const url = `/api/namespaces/${namespaceId}/tasks/${taskNumber}/close`;
  return requestJson<TaskSummary>(url, {
    method: "POST",
    body: JSON.stringify({
      reason: payload?.reason?.trim() || undefined,
      source: payload?.source?.trim() || "ui",
    }),
  });
}

export async function reopenNamespaceTask(
  namespaceId: NamespaceId,
  taskNumber: number,
  payload?: TaskReopenPayload,
) {
  if (!Number.isFinite(taskNumber)) {
    throw new ApiError("A numeric task identifier is required.", 400);
  }

  const url = `/api/namespaces/${namespaceId}/tasks/${taskNumber}/reopen`;
  return requestJson<TaskSummary>(url, {
    method: "POST",
    body: JSON.stringify({
      note: payload?.note?.trim() || undefined,
      source: payload?.source?.trim() || "ui",
    }),
  });
}

export async function restartNamespaceTask(
  namespaceId: NamespaceId,
  taskNumber: number,
  payload?: TaskRestartPayload,
) {
  if (!Number.isFinite(taskNumber)) {
    throw new ApiError("A numeric task identifier is required.", 400);
  }

  const url = `/api/namespaces/${namespaceId}/tasks/${taskNumber}/restart`;
  return requestJson<TaskSummary>(url, {
    method: "POST",
    body: JSON.stringify({
      note: payload?.note?.trim() || undefined,
      source: payload?.source?.trim() || "ui",
    }),
  });
}

export async function fetchNamespaceRepositories(namespaceId: NamespaceId) {
  const url = `/api/namespaces/${namespaceId}/repositories`;
  return fetchJson<WorkspaceRepository[]>(url);
}

export async function fetchNamespaceDocumentsTree(namespaceId: NamespaceId, signal?: AbortSignal) {
  const url = `/api/namespaces/${namespaceId}/documents/tree`;
  return fetchJson<WorkspaceDocumentsTreeResponse>(url, { signal });
}

export async function fetchNamespaceDocument(
  namespaceId: NamespaceId,
  documentPath: string,
  signal?: AbortSignal,
) {
  const normalizedPath = documentPath?.trim();
  if (!normalizedPath) {
    throw new ApiError("A document path is required.", 400);
  }

  const segments = normalizedPath.split("/").map((segment) => encodeURIComponent(segment));
  const url = `/api/namespaces/${namespaceId}/documents/content/${segments.join("/")}`;
  return fetchJson<WorkspaceDocumentContentResponse>(url, { signal });
}

export async function createNamespaceRepository(
  namespaceId: NamespaceId,
  payload: RepositoryPayload,
) {
  const url = `/api/namespaces/${namespaceId}/repositories`;
  return requestJson<WorkspaceRepository>(url, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateNamespaceRepository(
  namespaceId: NamespaceId,
  repositoryId: string,
  payload: RepositoryPayload,
) {
  const url = `/api/namespaces/${namespaceId}/repositories/${repositoryId}`;
  return requestJson<WorkspaceRepository>(url, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deleteNamespaceRepository(
  namespaceId: NamespaceId,
  repositoryId: string,
) {
  const url = `/api/namespaces/${namespaceId}/repositories/${repositoryId}`;
  await requestJson<void>(url, { method: "DELETE" });
}

/**
 * Attaches (or refreshes) an OAuth-backed credential on a repository from the
 * caller's connected provider account. Throws an ApiError with status 409 when
 * the caller has no live connection for `provider` yet — the caller should run
 * the provider's OAuth install flow (see createProviderInstall) and retry. See
 * NamespaceRepositoriesController.ReauthRepositoryAsync.
 */
export async function reauthNamespaceRepository(
  namespaceId: NamespaceId,
  repositoryId: string,
  provider?: string,
) {
  const url = `/api/namespaces/${namespaceId}/repositories/${repositoryId}/reauth`;
  return requestJson<WorkspaceRepository>(url, {
    method: "POST",
    body: JSON.stringify({ provider }),
  });
}

/** True when `error` is the "not connected yet" response from reauthNamespaceRepository. */
export function isRepositoryNotConnectedError(error: unknown) {
  return error instanceof ApiError && error.status === 409;
}

export async function fetchRepositoryTree(
  namespaceId: NamespaceId,
  repositoryId: string,
  options: RepositoryTreeRequestOptions = {},
) {
  const basePath = `/api/namespaces/${namespaceId}/repositories/${repositoryId}/tree`;
  const params = new URLSearchParams();

  const normalizedPath = options.path?.trim();
  if (normalizedPath) {
    params.set("path", normalizedPath);
  }

  const query = params.toString();
  const url = query ? `${basePath}?${query}` : basePath;
  return fetchJson<RepositoryTreeResponse>(url, { signal: options.signal });
}

export async function fetchRepositoryFileContent(
  namespaceId: NamespaceId,
  repositoryId: string,
  options: RepositoryFileRequestOptions,
) {
  const normalizedPath = options?.path?.trim();
  if (!normalizedPath) {
    throw new ApiError("A repository file path is required.", 400);
  }

  const basePath = `/api/namespaces/${namespaceId}/repositories/${repositoryId}/file`;
  const params = new URLSearchParams({ path: normalizedPath });
  const url = `${basePath}?${params.toString()}`;
  const response = await performRequest(url, { signal: options.signal });
  const content = await response.arrayBuffer();

  return {
    path: normalizedPath,
    content,
    contentType: response.headers.get("content-type") ?? "application/octet-stream",
    size: parseContentLength(response.headers.get("content-length")),
    etag: response.headers.get("etag"),
    lastModified: response.headers.get("last-modified"),
  } satisfies RepositoryFileContent;
}

export async function fetchRepositoryBranches(
  namespaceId: NamespaceId,
  repositoryId: string,
  signal?: AbortSignal,
) {
  const url = `/api/namespaces/${namespaceId}/repositories/${repositoryId}/branches`;
  return fetchJson<RepositoryBranch[]>(url, { signal });
}

export interface RepositoryBranchSource {
  source: string | null;
}

/**
 * Best-effort guess at which branch a head branch was forked from. The server
 * walks every other branch's merge-base with the head and picks the one whose
 * merge-base commit is most recent.
 */
export async function fetchRepositoryBranchSource(
  namespaceId: NamespaceId,
  repositoryId: string,
  head: string,
  signal?: AbortSignal,
) {
  const params = new URLSearchParams({ head });
  const url = `/api/namespaces/${namespaceId}/repositories/${repositoryId}/branches/source?${params.toString()}`;
  return fetchJson<RepositoryBranchSource>(url, { signal });
}

export async function fetchRepositoryCommits(
  namespaceId: NamespaceId,
  repositoryId: string,
  options: RepositoryCommitsRequestOptions = {},
) {
  const basePath = `/api/namespaces/${namespaceId}/repositories/${repositoryId}/commits`;
  const params = new URLSearchParams();

  const branch = options.branch?.trim();
  if (branch) {
    params.set("branch", branch);
  }

  if (typeof options.pageSize === "number" && options.pageSize > 0) {
    params.set("pageSize", options.pageSize.toString());
  }

  if (options.pageToken) {
    params.set("pageToken", options.pageToken);
  }

  const query = params.toString();
  const url = query ? `${basePath}?${query}` : basePath;
  return fetchJson<RepositoryCommitListResponse>(url, { signal: options.signal });
}

export async function fetchRepositoryDiff(
  namespaceId: NamespaceId,
  repositoryId: string,
  options: RepositoryDiffRequestOptions,
) {
  const head = options.head?.trim();
  if (!head) {
    throw new ApiError("A head commit or branch is required to fetch diffs.", 400);
  }

  const basePath = `/api/namespaces/${namespaceId}/repositories/${repositoryId}/diff`;
  const params = new URLSearchParams({ head });

  const base = options.base?.trim();
  if (base) {
    params.set("base", base);
  }

  const path = options.path?.trim();
  if (path) {
    params.set("path", path);
  }

  const url = `${basePath}?${params.toString()}`;
  return fetchJson<RepositoryDiffResponse>(url, { signal: options.signal });
}

export interface RepositoryFileDiffRequestOptions {
  head: string;
  base?: string;
  path: string;
  oldPath?: string;
  signal?: AbortSignal;
}

export async function fetchRepositoryFileDiff(
  namespaceId: NamespaceId,
  repositoryId: string,
  options: RepositoryFileDiffRequestOptions,
): Promise<RepositoryFileDiffResponse> {
  const head = options.head?.trim();
  if (!head) {
    throw new ApiError("A head commit or branch is required to fetch a file diff.", 400);
  }
  const path = options.path?.trim();
  if (!path) {
    throw new ApiError("A path is required to fetch a file diff.", 400);
  }

  const basePath = `/api/namespaces/${namespaceId}/repositories/${repositoryId}/diff/file`;
  const params = new URLSearchParams({ head, path });

  const base = options.base?.trim();
  if (base) params.set("base", base);

  const oldPath = options.oldPath?.trim();
  if (oldPath && oldPath !== path) params.set("oldPath", oldPath);

  const url = `${basePath}?${params.toString()}`;
  return fetchJson<RepositoryFileDiffResponse>(url, { signal: options.signal });
}

export async function requestRepositoryRefresh(
  namespaceId: NamespaceId,
  repositoryId: string,
  payload?: RepositoryRefreshPayload,
) {
  const url = `/api/namespaces/${namespaceId}/repositories/${repositoryId}/refresh`;
  const branch = payload?.branch?.trim();
  const body = branch ? JSON.stringify({ branch }) : undefined;

  return requestJson<RepositoryRefreshResponse>(url, {
    method: "POST",
    body,
    signal: payload?.signal,
  });
}

export async function fetchWorkspacePreferences() {
  return fetchJson<WorkspacePreferenceDto[]>("/api/workspace-preferences");
}

export async function fetchWorkspacePreference(scope: WorkspacePreferenceScope) {
  return fetchJson<WorkspacePreferenceDto>(`/api/workspace-preferences/${scope}`);
}

export async function saveWorkspacePreference(
  scope: WorkspacePreferenceScope,
  payload: WorkspacePreferenceUpdateRequest,
) {
  return requestJson<WorkspacePreferenceDto>(`/api/workspace-preferences/${scope}` , {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

function parseContentLength(value: string | null): number | null {
  if (!value) {
    return null;
  }

  const size = Number(value);
  return Number.isFinite(size) ? size : null;
}

export async function fetchSlackInstallation(orgId: string): Promise<SlackInstallation> {
  return fetchJson<SlackInstallation>(
    `/api/organizations/${encodeURIComponent(orgId)}/slack/installation`,
  );
}

export async function deleteSlackInstallation(orgId: string): Promise<void> {
  await requestJson(`/api/organizations/${encodeURIComponent(orgId)}/slack/installation`, {
    method: "DELETE",
  });
}

/**
 * Builds the URL the browser should navigate to in order to start the Slack
 * OAuth install flow. The target endpoint requires an authenticated user,
 * but this is a top-level navigation (set via window.location.href) so the
 * browser cannot attach an Authorization header. The API's JwtBearerEvents
 * hook in Program.cs already accepts `access_token` as a query parameter
 * (same mechanism used by the SSE endpoint); reuse that here.
 *
 * When a teamId is provided, it is forwarded as ?team=<id> so the kickoff
 * controller can pin Slack's /oauth/v2/authorize to that specific workspace
 * instead of letting Slack default to whichever workspace the user was most
 * recently signed into.
 */
export function buildSlackInstallUrl(orgId: string, teamId?: string | null): string {
  const base = buildAgentApiUrl(`/api/organizations/${encodeURIComponent(orgId)}/slack/install`);
  const params = new URLSearchParams();
  const trimmedTeam = teamId?.trim();
  if (trimmedTeam) {
    params.set("team", trimmedTeam);
  }
  const token = getApiAccessToken();
  if (token) {
    params.set("access_token", token);
  }
  const query = params.toString();
  if (!query) {
    return base;
  }
  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}${query}`;
}

// --- Doc-Code Audit ---

export async function fetchAuditRuns(namespaceId: NamespaceId) {
  return fetchJson<{ runs: import("@/types/audit").AuditRunSummary[] }>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/audit-runs`,
  );
}

export async function fetchAuditRun(namespaceId: NamespaceId, runId: string) {
  return fetchJson<import("@/types/audit").AuditRunSummary>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/audit-runs/${encodeURIComponent(runId)}`,
  );
}

export async function triggerAuditRun(
  namespaceId: NamespaceId,
  payload?: { auditMode?: import("@/types/audit").AuditMode },
) {
  // The backend's TriggerAuditRequest DTO defaults AuditMode to Full, so omitting
  // the body keeps the existing "no-arg" callers on the Full path. The drift-
  // staleness banner passes { auditMode: "Incremental" } to nudge the user
  // toward the lighter incremental run.
  return requestJson<import("@/types/audit").AuditRunSummary>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/audit-runs`,
    {
      method: "POST",
      headers: payload ? { "Content-Type": "application/json" } : undefined,
      body: payload ? JSON.stringify(payload) : undefined,
    },
  );
}

export async function fetchAuditStaleness(namespaceId: NamespaceId) {
  return fetchJson<import("@/types/audit").AuditStalenessResponse>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/audit/findings/staleness`,
  );
}

export async function cancelAuditRun(namespaceId: NamespaceId, runId: string) {
  return requestJson<{ message: string; runId: string }>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/audit-runs/${encodeURIComponent(runId)}/cancel`,
    { method: "POST" },
  );
}

export async function fetchAuditFindings(
  namespaceId: NamespaceId,
  runId: string,
  params?: { type?: string; status?: string; page?: number; pageSize?: number },
) {
  const searchParams = new URLSearchParams();
  if (params?.type) searchParams.set("type", params.type);
  if (params?.status) searchParams.set("status", params.status);
  if (params?.page) searchParams.set("page", String(params.page));
  if (params?.pageSize) searchParams.set("pageSize", String(params.pageSize));
  const query = searchParams.toString();
  const path = `api/namespaces/${encodeURIComponent(namespaceId)}/audit-runs/${encodeURIComponent(runId)}/findings${query ? `?${query}` : ""}`;
  return fetchJson<import("@/types/audit").AuditFindingsResponse>(path);
}

export async function fetchNamespaceUnresolvedFindings(
  namespaceId: NamespaceId,
  params?: { type?: string; page?: number; pageSize?: number },
) {
  const searchParams = new URLSearchParams();
  if (params?.type) searchParams.set("type", params.type);
  if (params?.page) searchParams.set("page", String(params.page));
  if (params?.pageSize) searchParams.set("pageSize", String(params.pageSize));
  const query = searchParams.toString();
  const path = `api/namespaces/${encodeURIComponent(namespaceId)}/audit/findings${query ? `?${query}` : ""}`;
  return fetchJson<import("@/types/audit").AuditFindingsResponse>(path);
}

export async function resolveAuditFinding(
  namespaceId: NamespaceId,
  runId: string,
  findingId: string,
  payload: import("@/types/audit").ResolveAuditFindingRequest,
) {
  return requestJson<import("@/types/audit").ResolveAuditFindingResponse>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/audit-runs/${encodeURIComponent(runId)}/findings/${encodeURIComponent(findingId)}/resolve`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) },
  );
}

export async function fetchAuditSettings(namespaceId: NamespaceId) {
  return fetchJson<import("@/types/audit").AuditSettings>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/audit-settings`,
  );
}

export async function updateAuditSettings(
  namespaceId: NamespaceId,
  payload: import("@/types/audit").AuditSettingsUpdateRequest,
) {
  return requestJson<import("@/types/audit").AuditSettings>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/audit-settings`,
    { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) },
  );
}

export async function fetchPrCheckSettings(namespaceId: NamespaceId) {
  return fetchJson<import("@/types/pr-checks").PrCheckSettings>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/pr-check-settings`,
  );
}

export async function updatePrCheckSettings(
  namespaceId: NamespaceId,
  payload: import("@/types/pr-checks").PrCheckSettingsUpdateRequest,
) {
  return requestJson<import("@/types/pr-checks").PrCheckSettings>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/pr-check-settings`,
    { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) },
  );
}

// --- Conventions ---

export async function fetchConventions(
  namespaceId: NamespaceId,
  status?: import("@/types/conventions").ConventionStatus | null,
) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return fetchJson<import("@/types/conventions").ConventionListResponse>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/conventions${qs}`,
  );
}

export async function createConvention(
  namespaceId: NamespaceId,
  payload: import("@/types/conventions").ConventionWriteRequest,
) {
  return requestJson<import("@/types/conventions").Convention>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/conventions`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) },
  );
}

export async function updateConvention(
  namespaceId: NamespaceId,
  conventionId: string,
  payload: import("@/types/conventions").ConventionWriteRequest,
) {
  return requestJson<import("@/types/conventions").Convention>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/conventions/${encodeURIComponent(conventionId)}`,
    { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) },
  );
}

export async function deleteConvention(namespaceId: NamespaceId, conventionId: string) {
  return requestJson<void>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/conventions/${encodeURIComponent(conventionId)}`,
    { method: "DELETE" },
  );
}

/** Approves a Proposed (improvement-generated) convention to Active. 404 unless Proposed. */
export async function approveConvention(namespaceId: NamespaceId, conventionId: string) {
  return requestJson<import("@/types/conventions").Convention>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/conventions/${encodeURIComponent(conventionId)}/approve`,
    { method: "POST" },
  );
}

/** Dismisses a Proposed convention to Archived. 404 unless Proposed. */
export async function dismissConvention(namespaceId: NamespaceId, conventionId: string) {
  return requestJson<import("@/types/conventions").Convention>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/conventions/${encodeURIComponent(conventionId)}/dismiss`,
    { method: "POST" },
  );
}

// --- Improvement settings (automatic improvements) ---

export async function fetchImprovementSettings(namespaceId: NamespaceId) {
  return fetchJson<import("@/types/improvements").ImprovementSettings>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/improvement-settings`,
  );
}

export async function updateImprovementSettings(
  namespaceId: NamespaceId,
  payload: import("@/types/improvements").ImprovementSettingsUpdate,
) {
  return requestJson<import("@/types/improvements").ImprovementSettings>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/improvement-settings`,
    { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) },
  );
}

// --- Improvement patterns (mined recurring mistakes) ---

export async function fetchImprovementPatterns(namespaceId: NamespaceId) {
  const response = await fetchJson<{
    patterns: import("@/types/improvements").ImprovementPattern[];
  }>(`api/namespaces/${encodeURIComponent(namespaceId)}/improvement-patterns`);
  return response.patterns;
}

/** Queues targeted convention generation for one pattern, bypassing the hit threshold. */
export async function improveImprovementPattern(namespaceId: NamespaceId, patternId: string) {
  return requestJson<import("@/types/improvements").ImprovementPattern>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/improvement-patterns/${encodeURIComponent(patternId)}/improve`,
    { method: "POST" },
  );
}

/** Flags the pattern Ignored: hits keep accumulating, but it is never improved automatically. */
export async function ignoreImprovementPattern(namespaceId: NamespaceId, patternId: string) {
  return requestJson<import("@/types/improvements").ImprovementPattern>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/improvement-patterns/${encodeURIComponent(patternId)}/ignore`,
    { method: "POST" },
  );
}

/** Puts an Ignored (or queued) pattern back to normal threshold-based generation. */
export async function restoreImprovementPattern(namespaceId: NamespaceId, patternId: string) {
  return requestJson<import("@/types/improvements").ImprovementPattern>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/improvement-patterns/${encodeURIComponent(patternId)}/restore`,
    { method: "POST" },
  );
}

// --- Library catalog (Advisory workspace) ---
// Proxied unchanged to the file service's AnalysisController; the API tier is
// just the auth-gated origin (docs/library-catalog.md).

export async function fetchLibraries(
  namespaceId: NamespaceId,
  params: import("@/types/libraries").FetchLibrariesParams = {},
) {
  const qs = new URLSearchParams();
  if (params.direct !== undefined && params.direct !== null) qs.set("direct", String(params.direct));
  if (params.ecosystem) qs.set("ecosystem", params.ecosystem);
  if (params.name) qs.set("name", params.name);
  if (params.vulnerableOnly) qs.set("vulnerableOnly", "true");
  if (params.outdatedOnly) qs.set("outdatedOnly", "true");
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.offset) qs.set("offset", String(params.offset));
  const suffix = qs.size > 0 ? `?${qs.toString()}` : "";
  return fetchJson<import("@/types/libraries").LibraryListResponse>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/analysis/libraries${suffix}`,
  );
}

/**
 * Advisories for one coordinate, most severe first. Unacknowledged and
 * unresolved by default — pass the flags to include triaged/upgraded-away
 * history in a detail view.
 */
export async function fetchLibraryAdvisories(
  namespaceId: NamespaceId,
  params: { libraryId?: string; includeResolved?: boolean; includeAcknowledged?: boolean } = {},
) {
  const qs = new URLSearchParams();
  if (params.libraryId) qs.set("libraryId", params.libraryId);
  if (params.includeResolved) qs.set("includeResolved", "true");
  if (!params.includeAcknowledged) qs.set("acknowledged", "false");
  return fetchJson<import("@/types/libraries").LibraryAdvisoryListResponse>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/analysis/library-advisories?${qs.toString()}`,
  );
}

/**
 * Acknowledge (or un-acknowledge) one advisory against one affected version.
 * An acknowledged row stays stored and is never resurfaced as new by a later
 * scan; the note records why it was triaged away.
 */
export async function acknowledgeLibraryAdvisory(
  namespaceId: NamespaceId,
  advisoryId: string,
  payload: import("@/types/libraries").AcknowledgeAdvisoryPayload,
) {
  return requestJson<void>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/analysis/library-advisories/${encodeURIComponent(advisoryId)}`,
    { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) },
  );
}

export async function runConventionDiscovery(
  namespaceId: NamespaceId,
  kind: import("@/types/conventions").ConventionDiscoveryKind = "full",
) {
  return requestJson<import("@/types/conventions").ConventionDiscoverResponse>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/conventions/discover`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kind }),
    },
  );
}

export async function fetchConventionDiscoveryStatus(namespaceId: NamespaceId) {
  return fetchJson<import("@/types/conventions").ConventionDiscoveryStatusResponse>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/conventions/discovery/status`,
  );
}

/**
 * Fetch the namespace's unresolved ConventionViolation findings. These are the
 * Open + Submitted findings the audit pipeline emitted against active
 * conventions. Each finding's `doc_file_path` is `convention:<conventionId>`,
 * so the caller groups by convention to show a per-rule violation count.
 */
export async function fetchConventionViolations(namespaceId: NamespaceId) {
  return fetchNamespaceUnresolvedFindings(namespaceId, {
    type: "ConventionViolation",
    pageSize: 500,
  });
}

// --- Analysis runs ---

export async function fetchAnalysisStatus(namespaceId: NamespaceId) {
  return fetchJson<import("@/types/analysis").AnalysisRunStatusResponse>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/analysis-runs/status`,
  );
}

/**
 * Query the analysis dependency graph. The API service proxies this to the
 * file service's /analysis/dependencies/query. On a 409 we resolve to a
 * `busy` shape instead of throwing so the UI can render a "rebuilding"
 * state in-place.
 */
export async function queryAnalysisDependencies(
  namespaceId: NamespaceId,
  request: import("@/types/analysis").AnalysisDependencyQueryRequest,
): Promise<import("@/types/analysis").AnalysisDependencyQueryResponse> {
  const normalizedNamespace = namespaceId?.trim();
  if (!normalizedNamespace) {
    throw new ApiError("A namespace is required to query dependencies.", 400);
  }

  const url = `/api/namespaces/${encodeURIComponent(normalizedNamespace)}/analysis/dependencies/query`;
  const body = JSON.stringify({
    queryType: request.queryType,
    target: request.target,
    depth: Math.max(1, Math.min(3, request.depth ?? 1)),
  });

  try {
    const results = await requestJson<import("@/types/analysis").AnalysisDependencyResult[]>(
      url,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        signal: request.signal,
      },
    );
    return { status: "ok", results: results ?? [] };
  } catch (error) {
    if (error instanceof ApiError && error.status === 409) {
      return { status: "busy", message: error.message };
    }
    throw error;
  }
}

/**
 * Ask the analysis graph which code symbols are linked to a documentation
 * file. Useful for populating a "Related" panel in the docs workspace —
 * for Markdown, `affected_by_file` always returns empty because docs don't
 * produce symbol rows; this endpoint walks the doc-symbol link edges
 * instead. On a 409 we resolve to `{ status: "busy" }`.
 */
export async function queryAnalysisDocumentLinks(
  namespaceId: NamespaceId,
  request: import("@/types/analysis").AnalysisDocumentLinksRequest,
): Promise<import("@/types/analysis").AnalysisDocumentLinksResponse> {
  const normalizedNamespace = namespaceId?.trim();
  if (!normalizedNamespace) {
    throw new ApiError("A namespace is required to query document links.", 400);
  }

  const normalizedPath = request.filePath?.trim();
  if (!normalizedPath) {
    throw new ApiError("filePath is required to query document links.", 400);
  }

  const url = `/api/namespaces/${encodeURIComponent(normalizedNamespace)}/analysis/document-links`;
  const body = JSON.stringify({ filePath: normalizedPath });

  try {
    const symbols = await requestJson<import("@/types/analysis").AnalysisDocumentLinkedSymbol[]>(
      url,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        signal: request.signal,
      },
    );
    return { status: "ok", symbols: symbols ?? [] };
  } catch (error) {
    if (error instanceof ApiError && error.status === 409) {
      return { status: "busy", message: error.message };
    }
    throw error;
  }
}

export async function uploadImage(
  namespaceId: string,
  file: File,
): Promise<{ url: string }> {
  const form = new FormData();
  form.append("file", file);
  return requestJson<{ url: string }>(
    `/api/namespaces/${encodeURIComponent(namespaceId)}/uploads/images`,
    {
      method: "POST",
      body: form,
    },
  );
}

// Agent skill zip uploads go through this client (not admin-api-client) so the
// FormData branch's UPLOAD_TIMEOUT_MS applies — skill archives can be up to
// 20 MB, far beyond the default request timeout's comfort zone.
export async function uploadNamespaceAgentSkillZip(
  namespaceId: string,
  file: File,
): Promise<import("@/types/agent-skills").AgentSkillSummary> {
  const form = new FormData();
  form.append("file", file);
  return requestJson<import("@/types/agent-skills").AgentSkillSummary>(
    `/api/namespaces/${encodeURIComponent(namespaceId)}/agent-skills/zip`,
    {
      method: "POST",
      body: form,
    },
  );
}

// Profile pictures live outside any namespace — the same face follows the user
// everywhere they appear, including namespaces a viewer can't see.
export async function uploadAvatarImage(file: File): Promise<{ url: string }> {
  const form = new FormData();
  form.append("file", file);
  return requestJson<{ url: string }>("/api/avatars", {
    method: "POST",
    body: form,
  });
}

export type UpdateAvatarResult = {
  pictureUrl: string | null;
  // The picture rides in a JWT claim, so the server hands back a token minted
  // from the updated record — without adopting it the UI would keep showing the
  // old avatar until the next sign-in.
  accessToken: string;
  expiresAt: string;
};

// Pass null to drop the custom avatar and fall back to initials (or, at the
// next sign-in, whatever the identity provider has).
export async function updateMyAvatar(pictureUrl: string | null): Promise<UpdateAvatarResult> {
  const result = await requestJson<UpdateAvatarResult>("/api/me/avatar", {
    method: "PUT",
    body: JSON.stringify({ pictureUrl }),
  });
  if (result?.accessToken) {
    setApiAccessToken(result.accessToken, { source: "refresh" });
  }
  return result;
}

// Fetches a namespace-scoped upload with the bearer token attached and returns
// an object-URL the consumer can hand to an <img> tag. Plain `<img src=...>`
// can't carry an Authorization header, so anything served from /api/uploads
// has to go through performRequest first.
export async function fetchAuthorizedImageBlobUrl(path: string): Promise<string> {
  const response = await performRequest(path, { method: "GET" });
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

// ---------------------------------------------------------------------------
// Onboarding state + GitHub OAuth + namespace self-serve creation.
// ---------------------------------------------------------------------------

import type {
  CreateGitProviderInstancePayload,
  CreateNamespacePayload,
  CreateOrganizationPayload,
  CreateOrganizationResponse,
  GitProviderInstance,
  GitProviderSource,
  OnboardingState,
  OnboardingUpdatePayload,
  ProviderConnection,
  ProviderInstallResponse,
  ProviderRepository,
} from "@/types/onboarding";

export async function fetchOnboardingState(): Promise<OnboardingState> {
  return fetchJson<OnboardingState>("/api/me/onboarding");
}

export async function updateOnboardingState(
  payload: OnboardingUpdatePayload,
): Promise<OnboardingState> {
  return requestJson<OnboardingState>("/api/me/onboarding", {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/**
 * Self-serve org creation for the onboarding "CreateOrganization" step. The
 * server responds with a fresh access token carrying the new "org" claim —
 * the caller is responsible for storing it so subsequent requests see the org.
 */
export async function createOnboardingOrganization(
  payload: CreateOrganizationPayload,
): Promise<CreateOrganizationResponse> {
  return requestJson<CreateOrganizationResponse>("/api/me/organization", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// --------------------------------------------------------------------------
// Git provider connections — provider-agnostic. `provider` is a SaaS slug
// ("github", "gitlab", "gitea", "bitbucket", "azure-devops") or a self-hosted
// instance slug. See ScirixAPIService/Controllers/GitProvidersController.
// --------------------------------------------------------------------------

/** Lists the git sources available to the caller's organization. */
export async function fetchGitProviderSources(): Promise<GitProviderSource[]> {
  const sources = await fetchJson<GitProviderSource[]>("/api/providers");
  return sources ?? [];
}

export async function fetchProviderConnection(provider: string): Promise<ProviderConnection | null> {
  try {
    return await fetchJson<ProviderConnection>(`/api/providers/${encodeURIComponent(provider)}/connection`);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function createProviderInstall(provider: string): Promise<ProviderInstallResponse> {
  return requestJson<ProviderInstallResponse>(`/api/providers/${encodeURIComponent(provider)}/install`, {
    method: "POST",
  });
}

export async function completeProviderCallback(
  provider: string,
  code: string,
  state: string,
): Promise<ProviderConnection> {
  return requestJson<ProviderConnection>(`/api/providers/${encodeURIComponent(provider)}/callback`, {
    method: "POST",
    body: JSON.stringify({ code, state }),
  });
}

export async function deleteProviderConnection(provider: string): Promise<void> {
  await requestJson<void>(`/api/providers/${encodeURIComponent(provider)}/connection`, {
    method: "DELETE",
  });
}

export async function fetchProviderRepositories(provider: string): Promise<ProviderRepository[]> {
  const repos = await fetchJson<ProviderRepository[]>(`/api/providers/${encodeURIComponent(provider)}/repos`);
  return repos ?? [];
}

// Legacy GitHub-named wrappers (kept for callers that haven't migrated).
export const fetchGitHubConnection = () => fetchProviderConnection("github");
export const createGitHubInstall = () => createProviderInstall("github");
export const completeGitHubCallback = (code: string, state: string) =>
  completeProviderCallback("github", code, state);
export const deleteGitHubConnection = () => deleteProviderConnection("github");
export const fetchGitHubRepositories = () => fetchProviderRepositories("github");

// --------------------------------------------------------------------------
// Self-hosted instance admin (org-scoped). See GitProviderInstancesController.
// --------------------------------------------------------------------------

export async function fetchGitProviderInstances(): Promise<GitProviderInstance[]> {
  const rows = await fetchJson<GitProviderInstance[]>("/api/organization/git-provider-instances");
  return rows ?? [];
}

export async function createGitProviderInstance(
  payload: CreateGitProviderInstancePayload,
): Promise<GitProviderInstance> {
  return requestJson<GitProviderInstance>("/api/organization/git-provider-instances", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function deleteGitProviderInstance(slug: string): Promise<void> {
  await requestJson<void>(`/api/organization/git-provider-instances/${encodeURIComponent(slug)}`, {
    method: "DELETE",
  });
}

/**
 * Enqueue a manual analysis run for a namespace. Idempotent at the server:
 * duplicate calls while a Pending/Running row already exists return the active
 * run rather than piling up additional queued runs.
 *
 * Set `incremental: true` to queue a run that only re-analyses files changed
 * since the last Completed run (server diffs each repo's current HEAD against
 * the SHAs captured at the previous completion). Falls back to a full rebuild
 * if no prior completed baseline is available.
 */
export async function triggerNamespaceAnalysis(
  namespaceId: NamespaceId,
  options?: { incremental?: boolean },
): Promise<{ runId: string; status: string; trigger?: string }> {
  const normalizedId = namespaceId?.trim();
  if (!normalizedId) {
    throw new ApiError("Namespace ID is required to trigger analysis.", 400);
  }
  const body = options?.incremental ? { incremental: true } : undefined;
  return requestJson<{ runId: string; status: string; trigger?: string }>(
    `/api/namespaces/${encodeURIComponent(normalizedId)}/analysis-runs`,
    {
      method: "POST",
      ...(body ? { body: JSON.stringify(body) } : {}),
    },
  );
}

/**
 * Self-serve namespace creation used by onboarding. When `repositories` is
 * provided, the server attaches them atomically and will derive `name` from the
 * first repo if not supplied. See ScirixAPIService/Controllers/NamespacesController.CreateWorkspaceAsync.
 */
export async function createNamespace(payload: CreateNamespacePayload): Promise<NamespaceSummary> {
  const body: Record<string, unknown> = {};
  if (payload.name !== undefined) body.name = payload.name;
  if (payload.description !== undefined) body.description = payload.description;
  if (payload.icon !== undefined) body.icon = payload.icon;
  if (payload.timezone !== undefined) body.timezone = payload.timezone;
  if (payload.repositories !== undefined) {
    body.repositories = payload.repositories.map((r) => ({
      provider: r.provider,
      cloneUrl: r.cloneUrl,
      defaultBranch: r.defaultBranch,
      fullName: r.fullName,
      private: r.private,
      description: r.description ?? null,
      sshPrivateKey: r.sshPrivateKey ?? null,
    }));
  }

  const response = await requestJson<RawNamespaceSummary>("/api/namespaces", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return normalizeNamespaceSummary(response);
}

/**
 * Reads how far along a namespace's database provisioning is. Creation returns
 * 202 with the namespace still `Pending`, so callers poll this until it reports
 * `Ready` (or `Failed`) rather than assuming the namespace is usable.
 */
export async function fetchNamespaceProvisioning(
  namespaceId: NamespaceId,
): Promise<NamespaceProvisioning> {
  const normalizedId = requireNamespaceId(namespaceId, "check provisioning");
  const raw = await fetchJson<Partial<NamespaceProvisioning> & { namespace_id?: string }>(
    `/api/namespaces/${normalizedId}/provisioning`,
  );
  return {
    namespaceId: raw.namespaceId ?? raw.namespace_id ?? namespaceId,
    state: raw.state ?? "Ready",
    error: raw.error ?? null,
  };
}

/* ------------------------------ goals ------------------------------ */

function requireNamespaceId(namespaceId: NamespaceId, action: string) {
  const normalized = namespaceId?.trim();
  if (!normalized) {
    throw new ApiError(`Namespace ID is required to ${action}.`, 400);
  }
  return encodeURIComponent(normalized);
}

export async function fetchNamespaceGoals(namespaceId: NamespaceId) {
  const ns = requireNamespaceId(namespaceId, "load goals");
  const response = await fetchJson<Goal[]>(`/api/namespaces/${ns}/goals`);
  return response ?? [];
}

export async function createNamespaceGoal(namespaceId: NamespaceId, payload: GoalCreatePayload) {
  const ns = requireNamespaceId(namespaceId, "create a goal");
  if (!payload?.title?.trim()) {
    throw new ApiError("Goals require a title.", 400);
  }
  return requestJson<Goal>(`/api/namespaces/${ns}/goals`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateNamespaceGoal(
  namespaceId: NamespaceId,
  goalId: string,
  payload: GoalUpdatePayload,
) {
  const ns = requireNamespaceId(namespaceId, "update a goal");
  return requestJson<Goal>(`/api/namespaces/${ns}/goals/${encodeURIComponent(goalId)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function executeNamespaceGoal(namespaceId: NamespaceId, goalId: string) {
  const ns = requireNamespaceId(namespaceId, "execute a goal");
  return requestJson<Goal>(`/api/namespaces/${ns}/goals/${encodeURIComponent(goalId)}/execute`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

/** "Start working": launches the autonomous goal-runner loop (Draft → Running). */
export async function startNamespaceGoal(namespaceId: NamespaceId, goalId: string) {
  const ns = requireNamespaceId(namespaceId, "start a goal");
  return requestJson<Goal>(`/api/namespaces/${ns}/goals/${encodeURIComponent(goalId)}/start`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

/** Cancels the active goal run and returns the goal to Draft. */
export async function cancelNamespaceGoalRun(namespaceId: NamespaceId, goalId: string) {
  const ns = requireNamespaceId(namespaceId, "cancel a goal run");
  return requestJson<Goal>(`/api/namespaces/${ns}/goals/${encodeURIComponent(goalId)}/cancel-run`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function fetchGoalRun(namespaceId: NamespaceId, goalId: string) {
  const ns = requireNamespaceId(namespaceId, "load goal run");
  return fetchJson<GoalRun>(`/api/namespaces/${ns}/goals/${encodeURIComponent(goalId)}/run`);
}

export async function fetchGoalActivity(namespaceId: NamespaceId, goalId: string) {
  const ns = requireNamespaceId(namespaceId, "load goal activity");
  const response = await fetchJson<GoalActivityItem[]>(
    `/api/namespaces/${ns}/goals/${encodeURIComponent(goalId)}/activity`,
  );
  return response ?? [];
}

/**
 * Archives a goal (sets it aside): cancels its active run so the goal runner
 * stops generating tasks, and stops agents working it by archiving its
 * still-active tasks. Reversible via updateNamespaceGoal({ archived: false }).
 */
export async function archiveNamespaceGoal(namespaceId: NamespaceId, goalId: string) {
  const ns = requireNamespaceId(namespaceId, "archive a goal");
  return requestJson<Goal>(`/api/namespaces/${ns}/goals/${encodeURIComponent(goalId)}/archive`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

/**
 * Hard-deletes a goal and cascades to its tasks, context items, and discussion
 * thread — a full reset so the flow can be re-run from a clean state.
 */
export async function deleteNamespaceGoal(namespaceId: NamespaceId, goalId: string) {
  const ns = requireNamespaceId(namespaceId, "delete a goal");
  await requestJson<void>(`/api/namespaces/${ns}/goals/${encodeURIComponent(goalId)}`, {
    method: "DELETE",
  });
}

export async function fetchGoalTasks(namespaceId: NamespaceId, goalId: string) {
  const ns = requireNamespaceId(namespaceId, "load goal tasks");
  const response = await fetchJson<TaskSummary[]>(
    `/api/namespaces/${ns}/goals/${encodeURIComponent(goalId)}/tasks`,
  );
  return response ?? [];
}

export async function fetchGoalContextItems(namespaceId: NamespaceId, goalId: string) {
  const ns = requireNamespaceId(namespaceId, "load goal context");
  const response = await fetchJson<GoalContextItem[]>(
    `/api/namespaces/${ns}/goals/${encodeURIComponent(goalId)}/context`,
  );
  return response ?? [];
}

export async function createGoalContextItem(
  namespaceId: NamespaceId,
  goalId: string,
  payload: GoalContextItemCreatePayload,
) {
  const ns = requireNamespaceId(namespaceId, "add goal context");
  if (!payload?.name?.trim()) {
    throw new ApiError("Context items require a name.", 400);
  }
  return requestJson<GoalContextItem>(
    `/api/namespaces/${ns}/goals/${encodeURIComponent(goalId)}/context`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export async function updateGoalContextItem(
  namespaceId: NamespaceId,
  goalId: string,
  itemId: string,
  payload: GoalContextItemUpdatePayload,
) {
  const ns = requireNamespaceId(namespaceId, "update goal context");
  return requestJson<GoalContextItem>(
    `/api/namespaces/${ns}/goals/${encodeURIComponent(goalId)}/context/${encodeURIComponent(itemId)}`,
    { method: "PATCH", body: JSON.stringify(payload) },
  );
}

export async function fetchGoalMessages(namespaceId: NamespaceId, goalId: string) {
  const ns = requireNamespaceId(namespaceId, "load the goal discussion");
  const response = await fetchJson<GoalMessage[]>(
    `/api/namespaces/${ns}/goals/${encodeURIComponent(goalId)}/messages`,
  );
  return response ?? [];
}

export async function fetchNamespaceTokenUsage(
  namespaceId: NamespaceId,
  range?: { from: string; to: string; lastMinutes?: number },
) {
  // A rolling sub-day window is served from raw logs; day bounds don't apply to it.
  const query = range?.lastMinutes
    ? `?lastMinutes=${range.lastMinutes}`
    : range
      ? `?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`
      : "";
  return fetchJson<import("@/types/namespaces").NamespaceTokenUsageResponse>(
    `api/namespaces/${encodeURIComponent(namespaceId)}/token-usage${query}`,
  );
}

export async function postGoalMessage(
  namespaceId: NamespaceId,
  goalId: string,
  content: string,
) {
  const ns = requireNamespaceId(namespaceId, "post to the goal discussion");
  const trimmed = content.trim();
  if (!trimmed) {
    throw new ApiError("Message content is required.", 400);
  }
  return requestJson<GoalMessage>(
    `/api/namespaces/${ns}/goals/${encodeURIComponent(goalId)}/messages`,
    { method: "POST", body: JSON.stringify({ content: trimmed }) },
  );
}

// --- Changes workspace: the commit index (plan §3.1) -----------------------
// Three reads over the analysis substrate's commit history. The listing is
// paged (the server clamps pageSize to 1..200); the activity endpoint returns
// gap-free density buckets so a wide window costs one cheap call instead of
// twenty pages.

export type CommitListQuery = {
  repositoryId?: string | null;
  author?: string | null;
  /** ISO-8601 instant, inclusive lower bound on `authoredAt`. */
  since?: string | null;
  /** ISO-8601 instant, inclusive upper bound on `authoredAt`. */
  until?: string | null;
  /** Case-insensitive substring on any changed file's path. */
  path?: string | null;
  page?: number | null;
  pageSize?: number | null;
  signal?: AbortSignal;
};

export type CommitActivityQuery = {
  since?: string | null;
  until?: string | null;
  bucket?: ChangesCommitActivityBucketSize | null;
  repositoryId?: string | null;
  signal?: AbortSignal;
};

/** Server-side clamp on `pageSize`, mirrored here so a caller cannot ask for a page the server will reshape. */
export const COMMIT_PAGE_SIZE_MAX = 200;

function setIfPresent(params: URLSearchParams, key: string, value: string | null | undefined) {
  const trimmed = value?.trim();
  if (trimmed) {
    params.set(key, trimmed);
  }
}

export async function fetchCommits(
  namespaceId: NamespaceId,
  query: CommitListQuery = {},
): Promise<ChangesCommitListResponse> {
  const ns = requireNamespaceId(namespaceId, "load commits");

  const params = new URLSearchParams();
  setIfPresent(params, "repositoryId", query.repositoryId);
  setIfPresent(params, "author", query.author);
  setIfPresent(params, "since", query.since);
  setIfPresent(params, "until", query.until);
  setIfPresent(params, "path", query.path);

  if (typeof query.page === "number" && Number.isFinite(query.page)) {
    params.set("page", String(Math.max(Math.trunc(query.page), 1)));
  }
  if (typeof query.pageSize === "number" && Number.isFinite(query.pageSize)) {
    params.set(
      "pageSize",
      String(Math.min(Math.max(Math.trunc(query.pageSize), 1), COMMIT_PAGE_SIZE_MAX)),
    );
  }

  const basePath = `/api/namespaces/${ns}/analysis/commits`;
  const queryString = params.toString();
  return fetchJson<ChangesCommitListResponse>(
    queryString ? `${basePath}?${queryString}` : basePath,
    { signal: query.signal },
  );
}

export async function fetchCommitActivity(
  namespaceId: NamespaceId,
  query: CommitActivityQuery = {},
): Promise<ChangesCommitActivityResponse> {
  const ns = requireNamespaceId(namespaceId, "load commit activity");

  if (query.bucket != null && query.bucket !== "day" && query.bucket !== "week") {
    throw new ApiError("Commit activity bucket must be 'day' or 'week'.", 400);
  }

  const params = new URLSearchParams();
  setIfPresent(params, "since", query.since);
  setIfPresent(params, "until", query.until);
  setIfPresent(params, "bucket", query.bucket);
  setIfPresent(params, "repositoryId", query.repositoryId);

  const basePath = `/api/namespaces/${ns}/analysis/commits/activity`;
  const queryString = params.toString();
  return fetchJson<ChangesCommitActivityResponse>(
    queryString ? `${basePath}?${queryString}` : basePath,
    { signal: query.signal },
  );
}

export async function fetchCommitDetail(
  namespaceId: NamespaceId,
  sha: string,
  signal?: AbortSignal,
): Promise<ChangesCommitRecord> {
  const ns = requireNamespaceId(namespaceId, "load a commit");

  // The server's own precondition (AnalysisController.GetCommitAsync). Checked
  // here so a stale `?commit=` deep link fails locally instead of round-tripping.
  const normalized = sha?.trim();
  if (!normalized || normalized.length < 7) {
    throw new ApiError("A commit hash of at least 7 characters is required.", 400);
  }

  return fetchJson<ChangesCommitRecord>(
    `/api/namespaces/${ns}/analysis/commits/${encodeURIComponent(normalized)}`,
    { signal },
  );
}

// --- Changelog generation (plan §3.4) ---------------------------------------
//
// THE ONLY MONEY-SPENDING CALL IN THIS CLIENT. `createChangelogRun` must be
// reachable ONLY from an explicit user confirmation — never from a query, a
// prefetch, a retry, or a `refetchInterval` (spec N1). It is a mutation for that
// reason, and the reads below are what polling uses.

/**
 * Queues one changelog run. Costs tokens.
 *
 * Both bounds are required and are sent verbatim: the server invents no default
 * window, because a default is a scope the user never saw.
 */
export async function createChangelogRun(
  namespaceId: NamespaceId,
  request: ChangelogRunRequest,
): Promise<ChangelogRunQueued> {
  const ns = requireNamespaceId(namespaceId, "generate a changelog");

  if (request.audience !== "developer" && request.audience !== "end_user") {
    throw new ApiError("Changelog audience must be 'developer' or 'end_user'.", 400);
  }
  if (!request.since || !request.until) {
    throw new ApiError("A changelog needs both a start and an end date.", 400);
  }

  return requestJson<ChangelogRunQueued>(`/api/namespaces/${ns}/changelog-runs`, {
    method: "POST",
    body: JSON.stringify({
      audience: request.audience,
      since: request.since,
      until: request.until,
      repositoryIds: request.repositoryIds ?? [],
    }),
  });
}

/** One run, including its entries and what it actually cost. Free. */
export async function fetchChangelogRun(
  namespaceId: NamespaceId,
  runId: string,
  signal?: AbortSignal,
): Promise<ChangelogRun> {
  const ns = requireNamespaceId(namespaceId, "load a changelog run");
  const normalized = runId?.trim();
  if (!normalized) {
    throw new ApiError("A changelog run id is required.", 400);
  }
  return fetchJson<ChangelogRun>(
    `/api/namespaces/${ns}/changelog-runs/${encodeURIComponent(normalized)}`,
    { signal },
  );
}

/** The namespace's recent runs, most recent first. Free. */
export async function fetchChangelogRuns(
  namespaceId: NamespaceId,
  query: { limit?: number; signal?: AbortSignal } = {},
): Promise<ChangelogRunListResponse> {
  const ns = requireNamespaceId(namespaceId, "load changelog runs");
  const params = new URLSearchParams();
  if (query.limit != null) params.set("limit", String(query.limit));

  const basePath = `/api/namespaces/${ns}/changelog-runs`;
  const queryString = params.toString();
  return fetchJson<ChangelogRunListResponse>(
    queryString ? `${basePath}?${queryString}` : basePath,
    { signal: query.signal },
  );
}

// --- Change impact reads (plan §3.2 / §3.3) ---------------------------------
//
// Both are deterministic GETs: no model is called, nothing is written, and both
// responses carry `generatedByAi: false` on the wire. **Neither may ever grow a
// method or a body** (spec N6) — that is the whole distinction the Changes
// workspace teaches between the free reads and the one paid action.

export type QaRetestQuery = {
  /** ISO-8601 instant, inclusive. */
  since: string;
  /** ISO-8601 instant, inclusive. */
  until: string;
  repositoryId?: string | null;
  signal?: AbortSignal;
};

/**
 * The commit's blast radius: touched symbols, their dependents, the services and
 * data stores their evidence reaches, and the feature / use-case documents
 * linked to them. Free.
 */
export type BlastRadiusOptions = {
  /** Also ask for boundary crossings (phase 4). Off by default. */
  crossings?: boolean;
};

export async function fetchBlastRadius(
  namespaceId: NamespaceId,
  sha: string,
  signal?: AbortSignal,
  options?: BlastRadiusOptions,
): Promise<BlastRadiusResponse> {
  const ns = requireNamespaceId(namespaceId, "load a commit's blast radius");

  // The server's own precondition (AnalysisChangesController: `invalid_sha`).
  // Checked here so a stale deep link fails locally instead of round-tripping.
  const normalized = sha?.trim();
  if (!normalized || normalized.length < 7) {
    throw new ApiError("A commit hash of at least 7 characters is required.", 400);
  }

  const suffix = options?.crossings ? "?crossings=true" : "";
  return fetchJson<BlastRadiusResponse>(
    `/api/namespaces/${ns}/analysis/commits/${encodeURIComponent(normalized)}/blast-radius${suffix}`,
    { signal },
  );
}

/**
 * The use-case documents implicated by every commit in a window. Free.
 *
 * Both bounds are required and sent verbatim: the server would otherwise resolve
 * a default window, and a default is a scope the user never chose.
 */
export async function fetchQaRetest(
  namespaceId: NamespaceId,
  query: QaRetestQuery,
): Promise<QaRetestResponse> {
  const ns = requireNamespaceId(namespaceId, "load the QA re-test list");

  if (!query?.since?.trim() || !query?.until?.trim()) {
    throw new ApiError("A QA re-test list needs both a start and an end instant.", 400);
  }

  const params = new URLSearchParams();
  setIfPresent(params, "since", query.since);
  setIfPresent(params, "until", query.until);
  setIfPresent(params, "repositoryId", query.repositoryId);

  return fetchJson<QaRetestResponse>(
    `/api/namespaces/${ns}/analysis/changes/qa-retest?${params.toString()}`,
    { signal: query.signal },
  );
}

export type ImpactFromItemQuery = {
  /** `item:<repoDir>/<ID>`, the lens node id of a requirement item. */
  itemRef: string;
  /** Optional window for the commits section; both or neither. */
  since?: string | null;
  until?: string | null;
  /** Also take one dependency hop out from the reached files (phase 4). Off by default. */
  blast?: boolean;
  signal?: AbortSignal;
};

/**
 * What changing a requirement item touches: the items below it, implementing
 * files, verifying tests, their modules, commits in the window, and the gaps
 * already recorded on any of it. Free, deterministic, `generatedByAi: false`.
 */
export async function fetchImpactFromItem(
  namespaceId: NamespaceId,
  query: ImpactFromItemQuery,
): Promise<ImpactFromItemResponse> {
  const ns = requireNamespaceId(namespaceId, "load a requirement's impact");

  const itemRef = query.itemRef?.trim();
  if (!itemRef || !itemRef.startsWith("item:")) {
    throw new ApiError("A requirement item reference (item:…) is required.", 400);
  }

  const params = new URLSearchParams();
  params.set("ref", itemRef);
  setIfPresent(params, "since", query.since);
  setIfPresent(params, "until", query.until);
  if (query.blast) params.set("blast", "true");

  return fetchJson<ImpactFromItemResponse>(
    `/api/namespaces/${ns}/analysis/impact/from-item?${params.toString()}`,
    { signal: query.signal },
  );
}

// ── Notification channels ───────────────────────────────────────────────────
// A namespace's outbound destinations: where broadcast notifications go (the
// agent's Notify tool in broadcast mode, library advisories, CI-failure
// acknowledgement threads). Distinct from the Slack *binding*, which routes
// inbound messages into the namespace. When a namespace has no channels the
// backend falls back to a Slack binding's webhook, so an empty list is normal.

export async function fetchNamespaceNotificationChannels(
  namespaceId: NamespaceId,
): Promise<NotificationChannel[]> {
  return fetchJson<NotificationChannel[]>(
    `/api/namespaces/${namespaceId}/notification-channels`,
  );
}

export async function createNamespaceNotificationChannel(
  namespaceId: NamespaceId,
  payload: NotificationChannelPayload,
): Promise<NotificationChannel> {
  return requestJson<NotificationChannel>(
    `/api/namespaces/${namespaceId}/notification-channels`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export async function deleteNamespaceNotificationChannel(
  namespaceId: NamespaceId,
  channelId: string,
): Promise<void> {
  await requestJson<void>(
    `/api/namespaces/${namespaceId}/notification-channels/${encodeURIComponent(channelId)}`,
    { method: "DELETE" },
  );
}

/** The Next page: the maturity ladder and the few things worth doing next (docs/workspaces/next-workspace.md). */
export async function fetchNamespaceNext(namespaceId: NamespaceId): Promise<NextResponse> {
  const body = await fetchJson<unknown>(`/api/namespaces/${encodeURIComponent(namespaceId)}/next`);
  if (!isNextResponse(body)) {
    throw new Error("Scirix answered with something this page cannot read.");
  }
  return body;
}
